import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, ReactElement, ReactNode, RefObject,
              SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Angebot, AngebotVersion, Ansprechpartner, BelegAblage,
              Benutzer, PositionsStand, Unternehmen,
              Vorlagenauswahl } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import type { FormularElement } from "@kundeninfo/ui/me-design";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, laufzeitVorschauWerte } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Vorgangsabschnitt } from "./Vorgangsabschnitt.js";
import { useSectionFolds, showFieldIn } from "./vorgangsabschnitt/sectionFolds.js";
import type { SectionFields, SectionFolds } from "./vorgangsabschnitt/sectionFolds.js";
import { SECTION_TEXT } from "./vorgangsabschnitt/texts.js";
import { partiesSummary, ratesSummary, termSummary } from "./vorgangsabschnitt/summaries.js";
import { FilesReadSection, useJumpAfterLeaving, counterProp, filesContent, findingsPerSection, hasText, itemsHead,
         openSectionsWithFindings, sectionRenderer, showFindingIn, summaryProp,
         useLoadedState } from "./vorgangsabschnitt/sections.js";
import type { FilesSource, SectionContent } from "./vorgangsabschnitt/sections.js";
import type { DocumentFilesClient } from "./Vorgangsdateien.js";
import { DOCUMENT_FILE_TEXT } from "./documentFiles/texts.js";
import { ROW_TEXT } from "../views/customerOverview/texts.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { BelegLayoutKnopf } from "./BelegLayout.js";
import type { BelegLayoutClient } from "./BelegLayout.js";
import { Zahlplan, ZAHLPLAN_KOPFFELDER } from "./Zahlplan.js";
import type { ZahlplanModalClient } from "./Zahlplan.js";
import { Anhaenge, ANHAENGE_KOPFFELDER } from "./Anhaenge.js";
import { RepresentativePairs, representativesClient } from "./RepresentativePairs.js";
import type { RepresentativePairsClient } from "./RepresentativePairs.js";
import type { AnhaengeClient } from "./Anhaenge.js";
import { formatBetrag, formatDatum } from "../format.js";
import { ANGEBOT_STATUS_ART, ANGEBOT_STATUS_LABEL } from "../vorgangsstatus.js";
import { RENEW_FILED_PDF_QUESTION, STATUS_CHANGE_TEXT } from "./documentQuestionTexts.js";
import type { OfferStatusTarget } from "./documentQuestionTexts.js";
import { apiMeldung, betragText, betragZahl, entwuerfeGleich,
         kopfBetragText, LAUFZEIT_KOPFFELDER,
         nettobetragLabel, oderNull,
         PDF_HINWEIS_ART, speichereBlob, speichereParteien, speichereZahlplan,
         useParteienGriff, useZahlplanGriff } from "./vorgangsdetail.js";
import type { PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `kang__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Reiters.
// Damit bringt der Baustein seine Gestalt überallhin mit, ohne dass components/ nach views/
// hineingreifen müsste.
import { hausfarbeChip } from "../markenfarben.js";
import { KiKnopf } from "./meHuellen.js";
import { errorProp } from "./fieldErrors.js";
import { laufzeitPaar } from "./laufzeitPaar.js";
import { HistoricalDetail } from "./historical/HistoricalDetail.js";
import type { HistoricalDetailClient } from "./historical/HistoricalDetail.js";
import { CopyOfferDialog } from "./offerSuccession/CopyOfferDialog.js";
import { ReplacesChips } from "./offerSuccession/ReplacesChips.js";
import { ReplacesField, replacesCandidates } from "./offerSuccession/ReplacesField.js";
import { SUCCESSION_TEXT, VERSION_NOTE_MAX_LENGTH } from "./offerSuccession/texts.js";
import { jumpToSeries, seriesAnchorId } from "./offerSuccession/seriesJump.js";
import { VersionNote } from "./offerSuccession/VersionNote.js";
import { VersionView } from "./offerSuccession/VersionView.js";
import "./AngebotDetail.css";

/**
 * Das ANGEBOTSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2).
 *
 * Herausgelöst aus views/KundeAngebote.tsx, damit der Akten-Reiter und der spätere
 * Angebots-BEREICH (Aufgabe 3) buchstäblich DASSELBE Detail zeichnen — nicht zwei Masken,
 * die sich anfangs gleichen und mit dem dritten Fix auseinanderlaufen. Der Umbau ist rein
 * strukturell: Es kommt kein Verhalten hinzu und es fällt keines weg.
 *
 * Was der Baustein trägt: die Kopfzeile des Vorgangs (Nummer, Kürzel, Titel, Status), die
 * Datenzeile (Betrag, Gültigkeit, Ansprechpartner), das Bearbeiten samt Positions-Editor
 * und Vorlagen-Anwenden, die Statusaktionen entlang der erlaubten Übergänge, „Als PDF"
 * mitsamt der `fields[]`-Befundliste und die Angebot→Auftrag-Naht.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Die Liste, das Anlegen, der
 * Meldungsstreifen und die Frage, WELCHER Vorgang gerade bearbeitet wird, gehören dem
 * Rahmen — er reicht sie als Eigenschaften und Rückrufe herein (`bearbeitung`, `gesperrt`,
 * `onBearbeiten`/`onSchliessen`/`onNeuLaden`/`onHinweis`/`onFortschreiben`). So trägt der
 * Bereich in Aufgabe 3 denselben Baustein, ohne dass hier ein Zweig „im Bereich" entsteht;
 * und ein späterer Rechnungs-Bereich könnte es genauso.
 *
 * Der Vorgang kommt als GELADENER Satz herein (`angebot`), nicht als blosse Kennung: Der
 * Reiter führt seine Liste bereits, und ein Nachladen je Zeile wäre bei zwanzig Angeboten
 * zwanzig zusätzliche Abrufe — eine Verhaltensänderung, die dieser Umbau ausdrücklich nicht
 * machen darf. Der Bereich sucht seinen Satz in Aufgabe 3 genauso aus seiner eigenen Liste.
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Anlegen bleiben beim
 *  Rahmen (siehe `AngeboteClient` in views/KundeAngebote.tsx). */
export type AngebotDetailClient = Pick<ApiClient,
  "getOffer" | "updateOffer" | "setOfferStatus"
  | "neueOfferVersion" | "kopiereOffer" | "listOfferVersions"
  // Kopieren und Nachfolge (Aufgabe 27): das Feld „Ersetzt“ — seine Kandidaten aus der Angebotsliste des Kunden.
  | "setOfferReplaces" | "listOffers"
  | "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates" | "applyTemplate"
  | "renderOffer"
  // Belegvorschau (24.09.2026): die Seitenbilder und die PDF-Ablage in der Kundenakte.
  | "vorschauOffer" | "getOfferAblage" | "createOfferAblage">
  & PositionsEditorClient
  & BelegParteienModalClient
  & BelegLayoutClient
  & ZahlplanModalClient
  // Der Kasten „Anlagen aus Textbausteinen" (465) (Paket „Textbausteine als Anhang", 24.09.2026) — fünf Wege, von
  // denen das Angebot vier benutzt (`anhangSichtbarkeit` gibt es nur am Auftrag; der
  // Ausschnitt bleibt trotzdem gemeinsam, damit es nur EINEN Kasten gibt).
  & AnhaengeClient
  // Die Ansprechpartner-Paare (Migration 069).
  & Partial<RepresentativePairsClient>
  // Der Abschnitt „Dateien“ (Aufgabe 25): Hochladen, Verknüpfen, Lösen, Löschen, Herunterladen, Ordnerbaum.
  & DocumentFilesClient
  // Das historische Angebot (Aufgabe 26): lesendes Detail und Bearbeiten im eigenen Panel.
  & HistoricalDetailClient<"offer">;

/**
 * Die AUSWAHLLISTEN, die Anlege- und Bearbeitungsmaske gemeinsam brauchen. Sie werden vom
 * RAHMEN geladen (`useAngebotsUmfeld`) und hier hereingereicht, nicht je Detail geholt:
 * Sonst zöge jede Listenzeile ihre eigenen Ansprechpartner, Benutzer, Platzhalter und
 * Vorlagen — vier Abrufe mal zwanzig Zeilen, für Daten, die für alle dieselben sind.
 */
export type AngebotsUmfeld = {
  kontakte: Ansprechpartner[];
  benutzer: Benutzer[];
  platzhalter: string[];
  vorlagen: Vorlagenauswahl[];
};

/**
 * Lädt die Auswahllisten einer Angebots-Ansicht: aktive Ansprechpartner des Kunden, aktive
 * Benutzer, die Platzhalternamen und die Vorlagen des betroffenen Unternehmens.
 *
 * Scheitert einer der Abrufe, bleibt die Ansicht bedienbar — die Felder sind optional, und
 * „ohne Vorlage" bleibt immer möglich. `vorlagenFirma` ist das Unternehmen, dessen Vorlagen
 * gerade gebraucht werden (leer = keine): Eine Vorlage gehört immer genau einer Firma, eine
 * gemeinsame Liste über alle wäre eine Auswahl, aus der die Hälfte 422 ergäbe.
 */
export function useAngebotsUmfeld(
  client: Pick<AngebotDetailClient,
    "listContacts" | "listUsers" | "listPlaceholders" | "listTemplates">,
  kundeId: string,
  vorlagenFirma: string,
): AngebotsUmfeld {
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([]);
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlagenauswahl[]>([]);

  useEffect(() => {
    let ab = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!ab) setKontakte(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  useEffect(() => {
    let ab = false;
    client.listUsers()
      .then(({ items }) => { if (!ab) setBenutzer(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    if (vorlagenFirma === "") { setVorlagen([]); return; }
    let ab = false;
    client.listTemplates(vorlagenFirma)
      .then(({ items }) => { if (!ab) setVorlagen(items); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten; „ohne Vorlage" bleibt möglich */ });
    return () => { ab = true; };
  }, [client, vorlagenFirma]);

  return { kontakte, benutzer, platzhalter, vorlagen };
}

type OfferStatus = Angebot["status"];

/**
 * Die erlaubten Statusübergänge — bewusst hier NACHGEBILDET, nicht aus @kundeninfo/domain
 * importiert: web darf nur `ui` und `api-client` importieren (check-boundaries). Die Regel
 * ist die aus der Spec (offerUebergangErlaubt): `DRAFT → SENT`; `SENT → ACCEPTED | REJECTED`;
 * aus jedem Nicht-Endzustand `→ WITHDRAWN`. Endzustände (ACCEPTED/REJECTED/WITHDRAWN) bieten
 * nichts an. Der Server prüft denselben Übergang noch einmal (422), das hier ist die
 * Bedienführung, nicht die Wahrheit.
 */
function erlaubteFolgestatus(von: OfferStatus): OfferStatusTarget[] {
  if (von === "DRAFT") return ["SENT", "WITHDRAWN"];
  if (von === "SENT") return ["ACCEPTED", "REJECTED", "WITHDRAWN"];
  // ACCEPTED, REJECTED, WITHDRAWN und SUPERSEDED sind Endzustände. SUPERSEDED wird
  // ausserdem NIE von Hand gesetzt — er entsteht allein durch „Neue Version".
  return [];
}

/** Der Verb-Knopftext je Zielstatus (Bedienführung: was der Klick TUT). */
const STATUS_AKTION: Record<OfferStatus, string> = {
  DRAFT: "Zurück in Entwurf",
  // NICHT „Senden": Der Knopf verschickt nichts, er setzt einen Status. Der alte Text
  // war ein Versprechen, das die Anwendung nicht einlöst (Spec, Abschnitt
  // „Oberfläche"); der Hinweis daneben sagt es noch einmal ausdrücklich.
  SENT: "Als gesendet markieren",
  ACCEPTED: "Annehmen",
  REJECTED: "Ablehnen",
  WITHDRAWN: "Zurückziehen",
  // Steht nur der Vollständigkeit halber in der Tabelle — SUPERSEDED taucht in
  // `erlaubteFolgestatus` nie auf und bekommt deshalb nie einen Knopf.
  SUPERSEDED: "Veraltet",
};

/** Der Satz neben „Als gesendet markieren" (Spec, wörtlich). */
const SENT_HINWEIS = "Setzt nur den Status. Es wird nichts versendet.";

/**
 * Der Versionszusatz — NACHGEBILDET wie `erlaubteFolgestatus`, weil `web` nur `ui` und
 * `api-client` einführen darf (check-boundaries). Die Elle ist `versionsZusatz` in
 * `@kundeninfo/domain`; dieselbe Regel, dieselben Worte: leer bei 1, sonst „v<n>".
 */
function versionsZusatz(versionNumber: number): string {
  return Number.isInteger(versionNumber) && versionNumber >= 2 ? `v${versionNumber}` : "";
}

/** Die Formularwerte. Alle Angaben sind Zeichenketten, damit ein geleertes Feld erst beim
 *  Absenden zu null/0 wird. */
export type AngebotEntwurf = {
  companyId: string;
  /** Die beim ANLEGEN gewählte Vorlage („" = ohne Vorlage). Sie geht als `vorlageId` mit —
   *  Anlegen und Anwenden in EINEM Zug; am bestehenden Angebot hat das Anwenden einen
   *  eigenen Weg (siehe `vorlageAnwenden`). */
  vorlageId: string;
  title: string;
  netAmount: string;
  contactId: string;
  responsibleUserId: string;
  validUntil: string;
  note: string;
  /** Rohtexte MIT ihren `#{…}`-Tokens — gespeichert wird genau das, aufgelöst wird erst
   *  fürs PDF (die Vorschau darunter ist nur eine Lesehilfe). */
  introText: string;
  outroText: string;
  /**
   * Die Laufzeitfelder aus Migration 031. Die WERTE als Zeichenkette, damit ein geleertes
   * Feld beim Absenden zu `null` wird statt zu 0 — „keine Mindestlaufzeit" und „null
   * Monate" sind zwei verschiedene Aussagen.
   *
   * Die EINHEITEN behalten ihren Wert auch bei leerem Feld: Sonst würde eine einmal
   * gewählte Angabe „Jahre" durch ein Leeren des Zahlenfeldes still zu „Monate"
   * (dieselbe Überlegung wie bei `noticeReference` seit Migration 008). Vorher gab es hier
   * nur zwei nackte Monatszahlen (`minimumTermMonths`/`renewalPeriodMonths`) und keine
   * Vertragslaufzeit — Migration 031 macht daraus vier Paare aus Zahl und Einheit.
   */
  termValue: string;
  termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string;
  minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string;
  renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string;
  noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
  /** Die Periodenregel des Zahlplans (Migration 026) — Kalender- oder Vertragsjahr. Sie
   *  steht am KOPF, weil sie für den ganzen Beleg gilt, nicht je Rate. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

export function leererAngebotEntwurf(
  companies: Unternehmen[], arbeitsunternehmenId: string | null,
): AngebotEntwurf {
  return {
    // Das Arbeitsunternehmen (ME-Etappe 1, Aufgabe 7), sofern es wählbar ist. Sonst wie
    // bisher: Bei genau einem Unternehmen (der häufige Fall: eine Kundenbeziehung) ist es
    // vorgewählt; bei mehreren muss bewusst gewählt werden.
    companyId: companies.find((c) => c.id === arbeitsunternehmenId)?.id
      ?? (companies.length === 1 ? companies[0]!.id : ""),
    vorlageId: "",
    title: "", netAmount: "", contactId: "", responsibleUserId: "", validUntil: "", note: "",
    introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: "CALENDAR_YEAR",
  };
}

export function angebotEntwurfAus(a: Angebot): AngebotEntwurf {
  return {
    companyId: a.companyId,
    // Beim Bearbeiten trägt das Formular KEINE Vorlage: Ein bestehendes Angebot wird nicht
    // „aus einer Vorlage angelegt" — dafür gibt es den Anwenden-Weg, der anhängt.
    vorlageId: "",
    title: a.title,
    // Deutsch geschrieben, weil deutsch gelesen wird (siehe betragText) — `String()` ergäbe
    // „200.55", und das läse `betragZahl` als 20055.
    netAmount: betragText(a.netAmount),
    contactId: a.contactId ?? "",
    responsibleUserId: a.responsibleUserId ?? "",
    validUntil: a.validUntil ?? "",
    note: a.note ?? "",
    introText: a.introText ?? "",
    outroText: a.outroText ?? "",
    termValue: a.termValue === null ? "" : String(a.termValue),
    termUnit: a.termUnit ?? "MONTHS",
    minimumTermValue: a.minimumTermValue === null ? "" : String(a.minimumTermValue),
    minimumTermUnit: a.minimumTermUnit ?? "MONTHS",
    renewalPeriodValue: a.renewalPeriodValue === null ? "" : String(a.renewalPeriodValue),
    renewalPeriodUnit: a.renewalPeriodUnit ?? "MONTHS",
    noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
    noticePeriodUnit: a.noticePeriodUnit ?? "MONTHS",
    noticeReference: a.noticeReference ?? "TERM_END",
    periodBasis: a.periodBasis,
  };
}

/**
 * Die ABSCHNITTE der Angebotsmaske in der Folge der Spec-Tabelle „Anwendung auf die drei Vorgangsdialoge“ (W14). Parteien,
 * Laufzeit und Zahlplan sind eigene Abschnitte mit eigener Fehlerzahl; der Kopfblock schrumpft auf die Kennwerte.
 */
export type AngebotAbschnitt =
  "head" | "parties" | "term" | "payment" | "note" | "intro" | "items" | "outro" | "representatives" | "textAttachments"
  | "files";

/**
 * Die Felder je Abschnitt: SERVER-Feldname → Endung der Feld-Id im Formular (Bauart `KopffeldTabelle`, vorgangsdetail.ts).
 * Beide Quellen von Feldbefunden — die Prüfung der Maske und der Server — gehen durch diese eine Tabelle, damit das
 * Auto-Aufklappen den RICHTIGEN Abschnitt öffnet. Ratenbefunde tragen einen Index (`raten[2].sharePercent`) und stehen
 * deshalb unter „raten“ (`sectionOf`, vorgangsabschnitt/sections.tsx).
 */
export const ANGEBOT_ABSCHNITTSFELDER: SectionFields<AngebotAbschnitt> = {
  head: { companyId: "company", title: "title", netAmount: "amount", contactId: "contact",
          responsibleUserId: "user", validUntil: "valid", replacesOfferId: "replaces" },
  // Die Parteien (Adressen Etappe 3) und der Zahlplan (Migration 026) werden in einem zweiten bzw. dritten Weg
  // gespeichert; ihre Befunde finden über dieselbe Tabelle ins Bild.
  parties: PARTEIEN_KOPFFELDER,
  term: { ...LAUFZEIT_KOPFFELDER, periodBasis: "periodbasis" },
  payment: ZAHLPLAN_KOPFFELDER,
  note: { note: "note" },
  intro: { introText: "intro" },
  items: {},
  outro: { outroText: "outro" },
  representatives: {},
  textAttachments: ANHAENGE_KOPFFELDER,
  files: {},
};

/**
 * Der Startzustand beim BEARBEITEN (Spec-Tabelle, Spalte „Start Bearbeiten“): Kopfdaten, Parteien, Laufzeit, Zahlung und
 * Anlagen zu — sie stehen meist schon, die Arbeit findet an den Positionen statt; die Texte offen, wenn Text da ist.
 */
export function ANGEBOT_START_BEARBEITEN(entwurf: AngebotEntwurf): Record<AngebotAbschnitt, boolean> {
  return {
    head: false, parties: false, term: false, payment: false,
    note: hasText(entwurf.note), intro: hasText(entwurf.introText), items: true, outro: hasText(entwurf.outroText),
    representatives: false, textAttachments: false, files: false,
  };
}

/** Beim ANLEGEN ist alles offen: Am leeren Vorgang gibt es nichts zu übergehen. */
export const OFFER_START_CREATE: Readonly<Record<AngebotAbschnitt, boolean>> = {
  head: true, parties: true, term: true, payment: true, note: true, intro: true, items: true, outro: true,
  representatives: true, textAttachments: true, files: true,
};

/** Die lesenden Abschnitte des geschlossenen Details. „Dateien“ ist dort bedienbar — Dateien drehen die Belegversion
 *  nicht (Spec „Abschnitt ‚Dateien‘“). */
export type OfferReadSection = "payment" | "representatives" | "textAttachments" | "files" | "versions";

/** Geschlossenes Detail: alles zu außer den Versionen, sobald es mehr als eine gibt. */
export function offerStartReadOnly(versionCount: number): Record<OfferReadSection, boolean> {
  return { payment: false, representatives: false, textAttachments: false, files: false, versions: versionCount >= 2 };
}

/**
 * Der Befund der Feldprüfung: der Satz für den Bearbeiter UND das Feld, um das es geht —
 * bei seinem SERVERNAMEN, damit Masken- und Serverweg durch dieselbe Tabelle laufen
 * (`ANGEBOT_ABSCHNITTSFELDER`).
 *
 * Das Feld kam mit dem eingefalteten Kopfblock dazu (Paket „Positionen mit Präsenz",
 * 08.09.2026): Alle drei Pflichtangaben stehen darin, und eingeklappt sähe niemand den
 * Satz an einem Feld, das gar nicht zu sehen ist.
 */
export type AngebotBefund = {
  meldung: string;
  feld: "companyId" | "title" | "netAmount";
};

/** Die gemeinsame Prüfung für Anlegen und Bearbeiten (null = in Ordnung). */
export function pruefeAngebot(
  entwurf: AngebotEntwurf, brauchtCompany: boolean,
): AngebotBefund | null {
  if (brauchtCompany && entwurf.companyId === "") {
    return { meldung: "Bitte ein Unternehmen wählen.", feld: "companyId" };
  }
  if (entwurf.title.trim() === "") {
    return { meldung: "Bitte einen Titel angeben.", feld: "title" };
  }
  if (Number.isNaN(betragZahl(entwurf.netAmount))) {
    return { meldung: "Bitte einen gültigen Betrag angeben.", feld: "netAmount" };
  }
  return null;
}

/**
 * Den Abschnitt auf das Feld öffnen, das ein Befund nennt, und es fokussieren — der eine Handgriff für BEIDE Quellen
 * (Maskenprüfung und Server-422). Steht das Feld in keinem Abschnitt oder trägt der Fehler keine Befunde (Netzabbruch),
 * geschieht nichts.
 */
export function zeigeAngebotsFeld(
  f: SectionFolds<AngebotAbschnitt>, praefix: string, feld: string,
): void {
  showFieldIn(f, ANGEBOT_ABSCHNITTSFELDER, praefix, feld);
}

export function zeigeAngebotsFehlerFeld(
  f: SectionFolds<AngebotAbschnitt>, praefix: string, fehler: unknown,
): void {
  showFindingIn(f, ANGEBOT_ABSCHNITTSFELDER, praefix, fehler);
}

export type AngebotKopffelder = {
  title: string; netAmount: number; contactId: string | null;
  responsibleUserId: string | null; validUntil: string | null; note: string | null;
  introText: string | null; outroText: string | null;
  termValue: number | null; termUnit: "MONTHS" | "YEARS" | null;
  minimumTermValue: number | null; minimumTermUnit: "MONTHS" | "YEARS" | null;
  renewalPeriodValue: number | null; renewalPeriodUnit: "MONTHS" | "YEARS" | null;
  noticePeriodValue: number | null;
  noticePeriodUnit: "MONTHS" | "YEARS" | null;
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END" | null;
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Die Kopffelder, die Anlegen und Ändern teilen (companyId gehört nur zum Anlegen — der
 *  Server lässt sie beim Ändern nicht zu). */
export function angebotKopffelder(entwurf: AngebotEntwurf): AngebotKopffelder {
  /** Ein Wert/Einheit-PAAR für den Server: leer heisst BEIDES null. Ausgeschrieben und
   *  nicht gestreut, weil `exactOptionalPropertyTypes` ein `undefined` anders behandelt
   *  als einen fehlenden Schlüssel. */
  function paar(wert: string, einheit: "MONTHS" | "YEARS") {
    const kern = wert.trim();
    return kern === ""
      ? { wert: null, einheit: null }
      : { wert: Number(kern), einheit };
  }
  const term = paar(entwurf.termValue, entwurf.termUnit);
  const mindest = paar(entwurf.minimumTermValue, entwurf.minimumTermUnit);
  const verlaengerung = paar(entwurf.renewalPeriodValue, entwurf.renewalPeriodUnit);
  const frist = paar(entwurf.noticePeriodValue, entwurf.noticePeriodUnit);
  return {
    title: entwurf.title.trim(),
    // Mit Positionen ist der Betrag berechnet; das Feld zeigt ihn nur an und schickt ihn
    // unverändert zurück (der Server nimmt genau diesen Wert an, siehe gepruefterBetrag).
    netAmount: betragZahl(entwurf.netAmount),
    contactId: oderNull(entwurf.contactId),
    responsibleUserId: oderNull(entwurf.responsibleUserId),
    validUntil: oderNull(entwurf.validUntil),
    note: oderNull(entwurf.note),
    introText: oderNull(entwurf.introText),
    outroText: oderNull(entwurf.outroText),
    termValue: term.wert, termUnit: term.einheit,
    minimumTermValue: mindest.wert, minimumTermUnit: mindest.einheit,
    renewalPeriodValue: verlaengerung.wert, renewalPeriodUnit: verlaengerung.einheit,
    noticePeriodValue: frist.wert, noticePeriodUnit: frist.einheit,
    // Der BEZUGSPUNKT hängt weiter an der Frist: Ohne Zahl gibt es keinen Stichtag.
    noticeReference: frist.wert === null ? null : entwurf.noticeReference,
    periodBasis: entwurf.periodBasis,
  };
}

/** Die Werte für die Platzhalter-Vorschau — genau das, was die Ansicht ohnehin geladen hat.
 *  Was fehlt (z. B. eine Anschrift), erscheint gar nicht erst und bleibt damit als
 *  sichtbares Token stehen. `angebot` ist beim ANLEGEN null — eine Angebotsnummer gibt es
 *  dann noch nicht. */
export function angebotVorschauWerte(arg: {
  entwurf: AngebotEntwurf;
  companies: Unternehmen[];
  kontakte: Ansprechpartner[];
  angebot: Angebot | null;
  kundeName?: string | undefined;
}): Record<string, string> {
  const { entwurf, companies, kontakte, angebot, kundeName } = arg;
  const werte: Record<string, string> = {
    ...laufzeitVorschauWerte({
      termValue: entwurf.termValue, termUnit: entwurf.termUnit,
      minimumTermValue: entwurf.minimumTermValue, minimumTermUnit: entwurf.minimumTermUnit,
      renewalPeriodValue: entwurf.renewalPeriodValue,
      renewalPeriodUnit: entwurf.renewalPeriodUnit,
      noticePeriodValue: entwurf.noticePeriodValue,
      noticePeriodUnit: entwurf.noticePeriodUnit,
      noticeReference: entwurf.noticeReference,
    }),
    "datum.heute": formatDatum(new Date().toISOString()),
  };
  if (kundeName !== undefined && kundeName !== "") werte["kunde.name"] = kundeName;
  const firma = companies.find((c) => c.id === entwurf.companyId);
  if (firma !== undefined) werte["firma.name"] = firma.name;
  const kontakt = kontakte.find((k) => k.id === entwurf.contactId);
  if (kontakt !== undefined) {
    werte["ansprechpartner.name"] = kontakt.anzeigename;
    // Migration 025: die Briefanrede steht NEBEN dem Namen (masterdataRules.ts) — sie ist
    // eine Ableitung aus Geschlecht, Titel und Namen, kein eigenes Eingabefeld.
    werte["ansprechpartner.anrede"] = kontakt.briefanrede;
  }
  if (angebot !== null) werte["angebot.nummer"] = angebot.displayNumber;
  if (entwurf.validUntil !== "") {
    werte["angebot.gueltigBis"] = formatDatum(entwurf.validUntil);
  }
  return werte;
}

export type AngebotFelderArgs = {
  praefix: string;
  mitUnternehmen: boolean;
  companies: Unternehmen[];
  umfeld: AngebotsUmfeld;
  entwurf: AngebotEntwurf;
  setEntwurf: Dispatch<SetStateAction<AngebotEntwurf>>;
  /** Hat der Vorgang Positionen? Dann ist der Nettobetrag berechnet und nur noch Anzeige. */
  hatPositionen: boolean;
  /** Die Währung des Vorgangs für die Zusammenfassung der Kopfdaten; beim Anlegen die Vorgabe (EUR). */
  currency?: string | undefined;
  /** Die Sammelmeldung am Fuss der Maske (Prüfung der Maske, Fehler ohne Feldbezug). */
  feldFehler: string | null;
  /**
   * Die Befunde JE FELD (ME-Etappe 3, Aufgabe 4): Servername → Satz, am Feld gezeigt
   * (`fehler` an me-eingabe/me-auswahl: aria-invalid + aria-describedby). Quelle sind die
   * 422-Befunde des Servers (`useFormularZustand.feldFehler`) und die Prüfung der Maske.
   */
  fehlerJeFeld?: Readonly<Record<string, string>> | undefined;
  /**
   * Die Formularzustände für das Raster der Maske (`me-formular`): `laeuft` sperrt die Felder
   * während eines Aufrufs, `fremderStand` zeigt nach einer 409 die Hinweiszeile mit
   * „Neu laden“. Die Anlage reicht nur `laeuft` herein.
   */
  formular?: {
    ref?: RefObject<FormularElement | null>;
    laeuft: boolean;
    fremderStand?: boolean;
    onNeuLaden?: () => void;
    hinweis?: ReactNode;
  } | undefined;
  vorschauWerte: Record<string, string>;
  /** Die Faltungen je Abschnitt — sie gehören der MASKE (siehe components/Kopffaltung.tsx), weil nur sie den
   *  Startzustand und die Rücksetzpunkte kennt. */
  faltungen: SectionFolds<AngebotAbschnitt>;
  /**
   * Die Abschnitte, deren Inhalt am GELADENEN Vorgang samt Version hängt — als Schlitz und nicht hier gezeichnet: Beim
   * ANLEGEN gibt es den Vorgang noch nicht, dort fehlen sie samt Abschnitt; die Folge der übrigen bleibt dieselbe. Die
   * Alternative wäre ein zweiter Aufbau der Maske je Weg, also genau die Kopie, die sich beim dritten Fix auseinanderlebt.
   *
   *  - `parteien`: Käufer, Anschriften, Bestellnummer (Adressen Etappe 3, zweiter Speicherweg `setBelegParteien`).
   *  - `zahlplan`: die Zahlungsvereinbarung (Migration 026, dritter Speicherweg).
   *  - `positionen`: der Positions-Editor zwischen Einleitungs- und Ausleitungstext (Entscheid Dirk 08.09.2026: dort
   *    wird die Arbeit geleistet).
   *  - `anhaenge`: die Anlagen aus Textbausteinen.
   *  - `dateien`: der Abschnitt „Dateien“ (`filesContent`).
   */
  parteien?: SectionContent | undefined;
  zahlplan?: SectionContent | undefined;
  positionen?: SectionContent | undefined;
  anhaenge?: SectionContent | undefined;
  /** Die Ansprechpartner-Paare (Migration 069) — nach dem Ausleitungstext, wie im Beleg. */
  ansprechpartner?: SectionContent | undefined;
  dateien?: SectionContent | undefined;
  /** Was das BEARBEITEN zusätzlich in den Kopfdaten zeigt — das Feld „Ersetzt“ (Aufgabe 27); beim Anlegen fehlt es. */
  headExtra?: ReactNode;
};

/**
 * Die Felder von Anlege- und Bearbeitungsmaske. Bewusst eine Funktion, die JSX ZURÜCKGIBT,
 * und keine eigene Komponente: eine im Render definierte Komponente bekäme bei jedem
 * Neuzeichnen eine neue Identität — React hängte ihren Teilbaum ab und neu auf, und der
 * Fokus spränge nach jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner).
 * Sie ist auch nach dem Herauslösen des Bausteins gemeinsam: Der Rahmen zeichnet mit ihr
 * seine ANLAGE, das Detail sein Bearbeiten.
 *
 * **Die Folge ist die der Spec-Tabelle „Anwendung auf die drei Vorgangsdialoge“** (W14): Kopfdaten, Parteien, Laufzeit
 * und Kündigung, Zahlungsvereinbarung, Notiz, Einleitungstext, POSITIONEN, Ausleitungstext, Anlagen aus Textbausteinen
 * — jeder ein `Vorgangsabschnitt` mit seiner Objektart (me-design 73d07f0). Sie steht genau EINMAL — hier — und gilt
 * damit für Akte wie Bereich, Anlegen wie Bearbeiten.
 */
export function angebotFormularFelder(a: AngebotFelderArgs): ReactElement {
  const { praefix, umfeld, entwurf, setEntwurf, faltungen } = a;
  const f = a.formular;
  const { section, slotSection, textSection, noteSection } = sectionRenderer(faltungen, praefix,
    findingsPerSection(ANGEBOT_ABSCHNITTSFELDER, a.fehlerJeFeld));
  /** Einleitungs- und Ausleitungstext: Rohtext mit Tokens, darunter die leichte Vorschau (dieselbe Gestalt wie am Auftrag
   *  und an der Rechnung, `textSection`). */
  function text(k: "intro" | "outro", field: "introText" | "outroText"): ReactElement {
    return textSection(k, SECTION_TEXT[k], {
      id: `${praefix}-${k}`, wert: entwurf[field], onChange: draftSetter(setEntwurf)(field),
      variablen: umfeld.platzhalter, werte: a.vorschauWerte,
    });
  }
  return (
    /* Das Raster der Maske (`me-formular`, ME-Etappe 3, Aufgabe 4) — mit der Beschriftung
       OBEN (Ruling: Die Hüllen beschriften sich selbst, „links“ erwartete `<label for>`-
       Paare). Es umschliesst die GANZE Maske, nicht nur die Kopffelder: Seine Hinweiszeile
       „fremder Stand“ muss sichtbar sein, auch wenn alle Abschnitte zugeklappt sind, und
       `zumErstenFehler()` sucht über alle Abschnitte. */
    <Formular beschriftung="oben"
      {...(f?.ref !== undefined ? { ref: f.ref } : {})}
      laeuft={f?.laeuft === true} fremderStand={f?.fremderStand === true}
      {...(f?.onNeuLaden !== undefined ? { onNeuLaden: f.onNeuLaden } : {})}
      {...(f?.hinweis !== undefined ? { hinweis: f.hinweis } : {})}>
      <div className="kang__maske">
        {section("head", "angebot", SECTION_TEXT.headTitle(entwurf.title), {
          inhalt: angebotKopffeldRaster(a),
          zusammenfassung: SECTION_TEXT.headSummary(kopfBetragText(entwurf.netAmount, a.currency),
            a.hatPositionen ? SECTION_TEXT.netComputed : SECTION_TEXT.net),
        })}
        {slotSection("parties", "kunde", SECTION_TEXT.parties, a.parteien)}
        {section("term", "vertrag", SECTION_TEXT.term,
          { inhalt: offerTermGrid(a), zusammenfassung: termSummary(entwurf) })}
        {slotSection("payment", "abrechnung", SECTION_TEXT.payment, a.zahlplan)}

        {/* Die Texte wachsen mit (Spec „Mitwachsende Textfelder“): der ganze Text ohne innere Bildlaufleiste, Mindesthöhe
            3 Zeilen; zugeklappt zeigt der Kopf die erste Zeile. Einleitungs- und Ausleitungstext sind Markdown mit Tokens
            und leichter Vorschau; die Notiz geht in keinen Beleg und bleibt ein schlichtes Feld. */}
        {noteSection("note", SECTION_TEXT.note,
          { id: `${praefix}-note`, wert: entwurf.note, onChange: draftSetter(setEntwurf)("note") })}
        {text("intro", "introText")}
        {slotSection("items", "position", SECTION_TEXT.items, a.positionen)}
        {text("outro", "outroText")}
        {slotSection("representatives", "kunde", SECTION_TEXT.representatives, a.ansprechpartner)}
        {slotSection("textAttachments", "textbaustein", SECTION_TEXT.textAttachments, a.anhaenge)}
        {slotSection("files", "datei", SECTION_TEXT.files, a.dateien)}

        {/* Die Sammelmeldung steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
            „Speichern" gedrückt, und dort wird sie gesucht. Befunde an einzelnen Feldern
            stehen AM FELD; betrifft einer ein Feld in einem zugeklappten Abschnitt, hat die
            Maske ihn bereits aufgeklappt (Fehlerzähler in der Kopfzeile, Fokus aufs Feld). */}
        {a.feldFehler !== null && <Hinweis art="fehler">{a.feldFehler}</Hinweis>}
      </div>
    </Formular>
  );
}

/** Ein Setzer je Entwurfsfeld — geteilt von den Rastern der Masken (Angebot und Auftrag). */
export function draftSetter<E>(setEntwurf: Dispatch<SetStateAction<E>>) {
  return <K extends keyof E>(feld: K) =>
    (wert: E[K]) => setEntwurf((v) => ({ ...v, [feld]: wert }));
}

/** Das zweispaltige Raster der KOPFDATEN — der Inhalt ihres Abschnitts. Eigene
 *  Funktion allein der Lesbarkeit halber: Sie hat keinen eigenen Zustand und keine eigene
 *  Identität (kein Komponententyp), zeichnet also weiterhin im selben Zug wie die Maske.
 *
 *  Seit ME-Etappe 3 (Aufgabe 4) auf den Hüllen: `Feld` (me-eingabe) für Text, Betrag,
 *  Datum und Zahlen, `Auswahl` (me-auswahl) statt der rohen `<select>`. Beide beschriften
 *  sich selbst; die Kennungen bleiben (`showFieldIn` fokussiert über sie). */
function angebotKopffeldRaster(a: AngebotFelderArgs): ReactElement {
  const { praefix, companies, umfeld, entwurf, hatPositionen } = a;
  const fj = a.fehlerJeFeld;
  const setze = draftSetter(a.setEntwurf);
  return (
    <div className="kang__felder">
      {a.mitUnternehmen && (
        <Auswahl id={`${praefix}-company`} name={`${praefix}-company`}
          beschriftung="Unternehmen" wert={entwurf.companyId} {...errorProp(fj?.companyId)}
          onGeaendert={(d) => setze("companyId")(d.wert)}>
          {companies.length !== 1 && <option value="">Bitte wählen …</option>}
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
          ))}
        </Auswahl>
      )}

      <Feld id={`${praefix}-title`} label="Titel" bearbeitbar wert={entwurf.title}
        onChange={setze("title")} {...errorProp(fj?.title)} />

      {/* Sobald der Vorgang Positionen hat, ist der Betrag BERECHNET: Das Feld zeigt ihn
          nur noch an. Von Hand gesetzt wäre er eine zweite Wahrheit — der Server weist ihn
          als Feldfehler ab (BETRAG_BERECHNET), und die Maske soll das gar nicht erst
          anbieten. `gesperrt` (readOnly + aria-disabled) statt `disabled`: Der Wert bleibt
          lesbar, kopierbar und wird beim Speichern unverändert mitgeschickt (genau das
          erwartet der Server). Ein Betrag ist TEXT (deutsches Komma), kein Zahlenfeld. */}
      <Feld id={`${praefix}-amount`} label={nettobetragLabel(hatPositionen)} bearbeitbar
        wert={entwurf.netAmount} onChange={setze("netAmount")} gesperrt={hatPositionen}
        {...errorProp(fj?.netAmount)} />

      <Auswahl id={`${praefix}-contact`} name={`${praefix}-contact`}
        beschriftung="Ansprechpartner" wert={entwurf.contactId}
        {...errorProp(fj?.contactId)} onGeaendert={(d) => setze("contactId")(d.wert)}>
        <option value="">— keiner —</option>
        {umfeld.kontakte.map((k) => (
          <option key={k.id} value={k.id}>{k.anzeigename}</option>
        ))}
      </Auswahl>

      <Auswahl id={`${praefix}-user`} name={`${praefix}-user`}
        beschriftung="Verantwortlich" wert={entwurf.responsibleUserId}
        {...errorProp(fj?.responsibleUserId)}
        onGeaendert={(d) => setze("responsibleUserId")(d.wert)}>
        <option value="">— niemand —</option>
        {umfeld.benutzer.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </Auswahl>

      <Feld id={`${praefix}-valid`} label="Gültig bis" typ="date" bearbeitbar
        wert={entwurf.validUntil} onChange={setze("validUntil")}
        {...errorProp(fj?.validUntil)} />

      {a.headExtra !== undefined && <div className="kang__feld kang__feld--breit">{a.headExtra}</div>}
    </div>
  );
}

/** Das Raster des Abschnitts LAUFZEIT UND KÜNDIGUNG: die vier Paare, der Bezugspunkt der Frist und die Periodenregel. */
function offerTermGrid(a: AngebotFelderArgs): ReactElement {
  const { praefix, entwurf } = a;
  const fj = a.fehlerJeFeld;
  const setze = draftSetter(a.setEntwurf);
  return (
    <div className="kang__felder">

      {/* DIE LAUFZEIT (Migration 031, Entscheid E1): vier Dauern, jede als Zahl PLUS
          Einheit. Die Vertragslaufzeit steht zuerst — sie ist die Aussage, die ein Kunde
          als erste sucht, und die Spec nennt sie ausdrücklich als erstes Feld. */}
      {laufzeitPaar({ block: "kang", id: `${praefix}-term`, label: "Vertragslaufzeit",
        einheitLabel: "Einheit der Vertragslaufzeit", wert: entwurf.termValue,
        einheit: entwurf.termUnit, onWert: setze("termValue"), onEinheit: setze("termUnit"),
        fehler: fj?.["termValue"], einheitFehler: fj?.["termUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-minterm`, label: "Mindestlaufzeit",
        einheitLabel: "Einheit der Mindestlaufzeit", wert: entwurf.minimumTermValue,
        einheit: entwurf.minimumTermUnit, onWert: setze("minimumTermValue"),
        onEinheit: setze("minimumTermUnit"),
        fehler: fj?.["minimumTermValue"], einheitFehler: fj?.["minimumTermUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-renewal`, label: "Verlängerung",
        einheitLabel: "Einheit der Verlängerung", wert: entwurf.renewalPeriodValue,
        einheit: entwurf.renewalPeriodUnit, onWert: setze("renewalPeriodValue"),
        onEinheit: setze("renewalPeriodUnit"),
        fehler: fj?.["renewalPeriodValue"], einheitFehler: fj?.["renewalPeriodUnit"] })}
      {laufzeitPaar({ block: "kang", id: `${praefix}-notice`, label: "Kündigungsfrist",
        einheitLabel: "Einheit der Kündigungsfrist", wert: entwurf.noticePeriodValue,
        einheit: entwurf.noticePeriodUnit, onWert: setze("noticePeriodValue"),
        onEinheit: setze("noticePeriodUnit"),
        fehler: fj?.["noticePeriodValue"], einheitFehler: fj?.["noticePeriodUnit"] })}

      {/* Der Bezugspunkt „Kündigung wirkt" bleibt UNVERÄNDERT an seiner Stelle hinter der
          Frist — er ist kein eigenes Wert/Einheit-Paar, sondern die Wahl, worauf sich die
          Frist bezieht. */}
      <div className="kang__feld kang__feld--breit">
        <Auswahl id={`${praefix}-notice-ref`} name={`${praefix}-notice-ref`}
          beschriftung="Kündigung wirkt" wert={entwurf.noticeReference}
          {...errorProp(fj?.noticeReference)}
          onGeaendert={(d) => setze("noticeReference")(
            d.wert === "CALENDAR_YEAR_END" ? "CALENDAR_YEAR_END" : "TERM_END")}>
          <option value="TERM_END">zum Ende der Laufzeit</option>
          <option value="CALENDAR_YEAR_END">zum Ende des Kalenderjahres</option>
        </Auswahl>
      </div>

      {/* Die Periodenregel des Zahlplans (Migration 026) — sie gilt für den ganzen
          Beleg, deshalb steht sie bei der Laufzeit und nicht je Rate in der Zahlungsvereinbarung. */}
      <div className="kang__feld kang__feld--breit">
        <Auswahl id={`${praefix}-periodbasis`} name={`${praefix}-periodbasis`}
          beschriftung="Leistungszeitraum" wert={entwurf.periodBasis}
          {...errorProp(fj?.periodBasis)}
          onGeaendert={(d) => setze("periodBasis")(
            d.wert === "CONTRACT_YEAR" ? "CONTRACT_YEAR" : "CALENDAR_YEAR")}>
          <option value="CALENDAR_YEAR">Kalenderjahr</option>
          <option value="CONTRACT_YEAR">Vertragsjahr</option>
        </Auswahl>
        <p className="kang__hinweis">
          Bei Kalenderjahr zählen erstes und letztes Jahr VOLL — Beträge und Rechnungen
          werden für das ganze Kalenderjahr aufgenommen.
        </p>
      </div>
    </div>
  );
}

export type AngebotDetailProps = {
  client: AngebotDetailClient;
  kundeId: string;
  /** Der Vorgang — fertig geladen, samt seiner `version` (siehe Kopf der Datei). */
  angebot: Angebot;
  /** Die Unternehmen mit Kundenbeziehung — für das Kürzel und die Platzhalter-Vorschau. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahllisten (`useAngebotsUmfeld`). */
  umfeld: AngebotsUmfeld;
  /** Steht GENAU DIESER Vorgang gerade in Bearbeitung? Die Entscheidung gehört dem Rahmen —
   *  nur so bleibt es bei einem offenen Formular je Ansicht. */
  bearbeitung: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo ein Formular offen ist? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`). */
  kundeName?: string | undefined;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach Statuswechsel, Speichern, 409). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht, nicht am
   *  einzelnen Vorgang. */
  onHinweis: (text: string | null) => void;
  /** Der Vorgang ist weitergedreht (Version/Betrag aus dem Positions-Editor, frischer Kopf
   *  nach „Vorlage anwenden"): Der Rahmen schreibt ihn in seine Liste fort und reicht ihn
   *  als `angebot` zurück herein. Ohne das rechneten die Statusknöpfe mit einer veralteten
   *  Version und meldeten „zwischenzeitlich geändert" für die eigene Änderung. */
  onFortschreiben: (angebot: Angebot) => void;
  /**
   * Der Vorgang ist durch eine ANDERE Kennung ERSETZT worden — „Neue Version" oder
   * „Kopieren" (Paket Angebotsversionen, Fixrunde 1 zu Aufgabe 6). Anders als
   * `onFortschreiben` (derselbe Vorgang, neuer Stand) wechselt hier die Kennung selbst:
   * Der Rahmen muss seinen Bezug — und, wo es eine Adresse gibt, sie selbst — auf den
   * NEUEN Vorgang umstellen und seine Liste nachziehen (Global Constraint „der Rahmen
   * lädt seine Liste neu und öffnet die neue Version"). Ein blosses `onNeuLaden` griffe
   * daneben: Es liest die Kennung, die der Rahmen GERADE führt — und das ist noch die
   * alte, jetzt abgelöste.
   */
  onErsetzt: (angebot: Angebot) => void;
  /** Die Angebot→Auftrag-Naht (Ruling P3A-B): am ACCEPTED-Angebot ruft „Auftrag anlegen"
   *  ihn mit den Angebotsdaten — der Baustein erzeugt selbst KEINEN Auftrag. */
  onAuftragAusAngebot: (angebot: Angebot) => void;
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ in den Kundenreiter „Dateien“ (Akte bzw. Bereich). */
  onOrdnerOeffnen: (folderId: string) => void;
  /** Der Vorgang steht allein (Bereich) statt als Zeile der Akten-Liste. Wirkt nur am historischen Vorgang: dort
   *  beginnen Notiz und Dateien offen (Aufgabe 26, Fix-Runde 1). Weglassen = Listenzeile. */
  standalone?: boolean;
};

/**
 * Das Präfix der Feld-Kennungen dieser Maske — einmal benannt, weil daraus auch das Ziel
 * des Auto-Aufklappens gebildet wird (`zeigeAngebotsFeld`). Zweimal getippt wäre es beim
 * nächsten Umbenennen einmal falsch, und dann klappte der Kopfblock auf ein Feld auf, das
 * es nicht gibt.
 */
const BEARBEITEN_PRAEFIX = "kang-bearbeiten";

/**
 * Die Beschriftungen je SERVER-Feldname — für die Sammelmeldung des Formularzustands
 * (`useFormularZustand.beschriftungen`, Ruling Aufgabe 3/4): Ein Befund, der an keinem Feld
 * steht, nennt die Beschriftung, die der Benutzer sieht — nie den technischen Namen.
 * Geteilt mit der Auftragsmaske (`AUFTRAG_BESCHRIFTUNGEN` baut darauf auf).
 */
export const VORGANG_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  companyId: "Unternehmen", title: "Titel", netAmount: "Nettobetrag",
  contactId: "Ansprechpartner", responsibleUserId: "Verantwortlich",
  introText: "Einleitungstext", outroText: "Ausleitungstext",
  termValue: "Vertragslaufzeit", termUnit: "Einheit der Vertragslaufzeit",
  minimumTermValue: "Mindestlaufzeit", minimumTermUnit: "Einheit der Mindestlaufzeit",
  renewalPeriodValue: "Verlängerung", renewalPeriodUnit: "Einheit der Verlängerung",
  noticePeriodValue: "Kündigungsfrist", noticePeriodUnit: "Einheit der Kündigungsfrist",
  noticeReference: "Kündigung wirkt", periodBasis: "Leistungszeitraum",
};

export const ANGEBOT_BESCHRIFTUNGEN: Readonly<Record<string, string>> = {
  ...VORGANG_BESCHRIFTUNGEN, validUntil: "Gültig bis", note: "Notiz", replacesOfferId: SUCCESSION_TEXT.field,
};

/**
 * Die Felder, die die BEARBEITUNGSMASKE als Hülle mit eigenem Fehlerplatz zeichnet — ein
 * Befund zu ihnen steht AM FELD (`useFormularZustand.bekannteFelder`). Alles andere (Notiz,
 * Einleitungs-/Ausleitungstext, die beim Bearbeiten nicht gezeigte Firma) landet als
 * „<Beschriftung>: <Satz>“ in der Sammelmeldung am Fuss — kein Befund verschwindet still.
 */
const ANGEBOT_FELDER_AM_FELD: readonly string[] = Object.keys(ANGEBOT_BESCHRIFTUNGEN)
  .filter((f) => !["companyId", "note", "introText", "outroText"].includes(f));

/** Der Fehlschlag eines FOLGEWEGES (Parteien, Zahlplan) im Speichern: Sein Befund steht
 *  schon im Block (`befunde`), der Formularzustand braucht nur noch den Satz. */
class FolgewegFehler extends Error {}

/** Was ein gescheiterter Folgeweg an den Formularzustand weitergibt: eine 409 als SIE SELBST
 *  (dann gilt der fremde Stand für die ganze Maske), alles andere als Satz. */
export function folgewegFehler(meldung: string, fehler: unknown): Error {
  return fehler instanceof ApiError && fehler.status === 409
    ? fehler : new FolgewegFehler(meldung);
}

/**
 * Das Angebotsdetail: ein historisches Angebot (Altbestand ohne Positionen und Beleg, Aufgabe 26) zeichnet sein eigenes,
 * schmales Detail mit „Bearbeiten“ als einziger Aktion; jedes andere die volle Maske. Die Art eines Vorgangs wechselt nie
 * — die Weiche hängt keinen Zustand um.
 */
export function AngebotDetail(p: AngebotDetailProps): ReactElement {
  if (p.angebot.kind === "HISTORICAL") {
    return (
      <HistoricalDetail client={p.client} kundeId={p.kundeId} art="offer" record={p.angebot} companies={p.companies}
        umfeld={p.umfeld} bearbeitung={p.bearbeitung} gesperrt={p.gesperrt} onBearbeiten={p.onBearbeiten}
        onSchliessen={p.onSchliessen} onNeuLaden={p.onNeuLaden} onOrdnerOeffnen={p.onOrdnerOeffnen}
        standalone={p.standalone === true}
        onOpenSeries={(offerId) => { void jumpToSeries(p.angebot, offerId, p); }} />
    );
  }
  return <OfferDocumentDetail {...p} />;
}

function OfferDocumentDetail(p: AngebotDetailProps): ReactElement {
  const { client, kundeId, angebot, companies, umfeld, bearbeitung, gesperrt } = p;

  const [entwurf, setEntwurf] = useState<AngebotEntwurf>(() => angebotEntwurfAus(angebot));
  /**
   * Die Faltungen je Abschnitt beim BEARBEITEN (`ANGEBOT_START_BEARBEITEN`): Kopfdaten, Laufzeit und Kündigungsfrist
   * stehen meist schon, die Arbeit findet an den Positionen statt (Entscheid Dirk 08.09.2026). Der Start richtet sich nach
   * dem GESPEICHERTEN Vorgang — beim Rücksetzen gilt so der Vorgang, der gerade aufgeschlagen wird.
   */
  const faltungen = useSectionFolds(ANGEBOT_START_BEARBEITEN(angebotEntwurfAus(angebot)));
  /** Die Faltungen der LESENDEN Abschnitte im geschlossenen Detail (Gesamtreview, I2): Zahlungsvereinbarung und
   *  Anlagen hängen ihren Inhalt erst beim Öffnen ein — sonst riefe jede Zeile der Liste sofort `getZahlplan` und
   *  `getAnhaenge` ab, obwohl kaum je jemand hinsieht. */
  const readSections = useSectionFolds(offerStartReadOnly(angebot.versionCount));
  /** Was die Abschnitte Parteien, Zahlungsvereinbarung und Anlagen zugeklappt sagen: ihr geladener Stand (die
   *  Entwürfe von Parteien und Zahlplan führen die Griffe als Zustand, `draft`). */
  const loaded = useLoadedState();
  /** Der Abschnitt „Dateien“ — im Panel wie im geschlossenen Detail derselbe Baustein; an einer älteren Version lesend
   *  (Spec „Abschnitt ‚Dateien‘“: Verknüpfen und Lösen nur an der jüngsten, sonst 422 ANGEBOT_VERALTET). */
  const files: FilesSource = {
    client, kundeId, bezug: { art: "offers", id: angebot.id }, firmen: companies,
    vorgangName: ROW_TEXT.firstLink("OFFER", angebot.displayNumber),
    lesend: angebot.istJuengste ? null : DOCUMENT_FILE_TEXT.readOnlyOlder, onOrdnerOeffnen: p.onOrdnerOeffnen,
  };
  /** Der Befund der MASKENprüfung (`pruefeAngebot`) — steht am Feld UND am Fuss. Befunde
   *  des Servers führt der Formularzustand (`formular.feldFehler`/`formular.meldung`). */
  const [pruefung, setPruefung] = useState<AngebotBefund | null>(null);
  /** Der Positionsstand des Vorgangs — der Editor meldet ihn nach jedem Schritt. Er
   *  entscheidet, ob der Nettobetrag von Hand änderbar ist. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Welche Vorlage der Anwenden-Dialog gerade anbietet (null = zu). */
  const [anwendenWahl, setAnwendenWahl] = useState<string | null>(null);
  /** Lief im offenen Subdialog schon ein „Anwenden“? Dann gehört die Meldung des
   *  Formularzustands IHM (und steht dort), sonst der Maske darunter. */
  const [anwendenVersucht, setAnwendenVersucht] = useState(false);
  /*
   * Die Fokus-Rückgabe an „Vorlage anwenden“ nach dem Schliessen des Subdialogs macht
   * me-dialog selbst (aff0190, P4-1 in 8cc13b0): Es merkt sich den Auslöser auch in Safari
   * über den letzten Zeiger-Klick und wiederholt die Rückgabe, solange der Knopf beim
   * Aushängen noch gesperrt ist. Die eigene Rückgabe, die hier seit der Fixrunde 1 stand,
   * entfällt (P8). Setzt ein 422-Befund an einem Feld der Maske den Fokus dorthin, rührt
   * das Element ihn nicht an — es gibt nur zurück, was sonst verloren wäre.
   */
  /** Zählt hoch, sobald eine Vorlage angewandt wurde: Er steckt im `key` des Editors und
   *  hängt ihn damit neu auf, sodass er die angehängten Zeilen frisch lädt. Der Editor führt
   *  seinen Stand selbst — ohne das zeigte er nach dem Anwenden die alte Liste. */
  const [positionenNeuLaden, setPositionenNeuLaden] = useState(0);
  /** Läuft gerade eine Aktion an DIESEM Vorgang (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /** Die Rückmeldung des „Als PDF"-Knopfes — MIT ihrer Art (siehe PDF_MELDUNGS_STIL). */
  const [pdfMeldung, setPdfMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);
  /**
   * Der Ablagestand DIESES Angebots: `undefined` heisst „noch nicht beantwortet",
   * `null` „es gibt keine". Dieselbe Dreiteilung wie an der Rechnung
   * (`RechnungDetail`) — ein blosses `null` für beides hiesse, dass die Maske beim
   * Aufschlagen „nicht abgelegt" behauptet, bevor sie gefragt hat.
   */
  const [ablage, setAblage] = useState<BelegAblage | null | undefined>(undefined);
  /** Der Entwurf des Kopfblocks PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Der Entwurf des Abschnitts ZAHLUNGSVEREINBARUNG — dieselbe Bauart, DRITTER
   *  Speicherweg (`useZahlplanGriff`, vorgangsdetail.ts). */
  const zahlplan = useZahlplanGriff();
  /** Zählt hoch, wenn „Neu laden“ den Serverstand holt: Er steckt im `key` von Parteien,
   *  Zahlplan und Anhängen und hängt sie damit neu auf — sie führen ihren Stand selbst. */
  const [standNeuLaden, setStandNeuLaden] = useState(0);
  /** Die Ansprechpartner-Paare (Migration 069): schreiben selbst mit der Angebotsversion (wie die Anlagen) und tragen sie
   *  über `onFortschreiben` zurück; laden erst beim Aufklappen. */
  const apClient = representativesClient(client);
  const representativesBox = (open: boolean, idPrefix: string, covered?: boolean): ReactNode => open && apClient !== null && (
    <RepresentativePairs key={`${angebot.id}-ansprechpartner-${String(standNeuLaden)}`} client={apClient}
      bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
      idPraefix={idPrefix} {...(covered === undefined ? {} : { verdeckt: covered })}
      version={angebot.version}
      onVersionChange={(version) => { p.onFortschreiben({ ...angebot, version }); }}
      onLoaded={loaded.onRepresentativesLoaded} />
  );
  /**
   * Das Feld „Ersetzt“ (Aufgabe 27): der Wert im Panel und die Angebotsliste des Kunden, aus der die Kandidaten kommen
   * (`replacesCandidates`). Geladen erst mit offenem Panel — sonst ein Abruf je Listenzeile. Geschrieben wird beim
   * Speichern und nur bei Änderung (Dirk 216c: „Speichern“ IST die Bestätigung).
   */
  const savedReplaces = angebot.replaces?.offerId ?? null;
  const [replacesValue, setReplacesValue] = useState<string | null>(savedReplaces);
  const [customerOffers, setCustomerOffers] = useState<Angebot[]>([]);
  useEffect(() => {
    if (!bearbeitung) return undefined;
    let gone = false;
    client.listOffers(kundeId)
      .then(({ items }) => { if (!gone) setCustomerOffers(items); })
      .catch(() => { /* Nebenauskunft — die Maske bleibt bedienbar, das Feld bietet nur „keines“ */ });
    return () => { gone = true; };
  }, [client, kundeId, bearbeitung]);
  /** Ist die Kopierrückfrage (`CopyOfferDialog`) offen? */
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  /** Die ältere Version, die „Ansehen“ gerade lesend zeigt (Dirk 484) — `null`, solange keine offen ist. */
  const [versionView, setVersionView] = useState<AngebotVersion | null>(null);

  /**
   * Der Weg, den der Formularzustand gerade geht — Speichern ODER Vorlage anwenden. Beide
   * schreiben an DEMSELBEN Vorgang mit derselben Version, also teilen sie sich EINEN
   * Zustand: Eine 409 auf dem einen sperrt auch den anderen, bis „Neu laden“.
   */
  const weg = useRef<() => Promise<boolean>>(async () => true);
  /**
   * Die Formularzustände (ME-Etappe 3, Aufgabe 4) — ersetzt den eigenen 409-Code: Eine 409
   * schliesst die Maske nicht mehr und lädt nicht still nach, sondern setzt den „fremden
   * Stand“ (Felder gesperrt, die Eingaben bleiben lesbar, „Neu laden“ in der Hinweiszeile).
   * Eine 422 legt die Befunde an die Felder, klappt den Kopfblock auf, wenn einer darin
   * steht, und springt zum ersten (Review-Fokus 3).
   */
  const formular = useFormularZustand<boolean>({
    speichern: () => weg.current(),
    neuLaden: async () => {
      const frisch = await client.getOffer(kundeId, angebot.id);
      p.onFortschreiben(frisch);
      setEntwurf(angebotEntwurfAus(frisch));
      // Auch der Wert im Feld „Ersetzt“ (A-W1): Sonst dreht das nächste Speichern eine fremde Nachfolge still zurück.
      setReplacesValue(frisch.replaces?.offerId ?? null);
      setPruefung(null);
      setPositionen(null);
      setPositionenNeuLaden((n) => n + 1);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      setStandNeuLaden((n) => n + 1);
    },
    bekannteFelder: ANGEBOT_FELDER_AM_FELD,
    beschriftungen: ANGEBOT_BESCHRIFTUNGEN,
  });
  const speichert = formular.laeuft;

  /** Die Versionen der Reihe für den Abschnitt „Versionen" — `null` heisst „noch nicht
   *  geladen". Geladen wird von selbst NUR, wenn es mehr als eine gibt (`versionCount`),
   *  sonst erst, wenn jemand den Abschnitt öffnet: Der Regelfall ist ein Angebot mit genau
   *  einer Fassung, und ein Abruf je Listenzeile dafür wäre reine Last. */
  const [versionen, setVersionen] = useState<AngebotVersion[] | null>(null);

  /**
   * Kein UI-Zustand überlebt den Wechsel der Angebotskennung: Der Effekt setzt beim
   * Wechsel ZUERST zurück und zählt beim Abräumen hoch (`lauf`-Ref wie in
   * `AdressListe.tsx`), damit eine spät eintreffende Antwort des VORIGEN Angebots die
   * Liste des jetzigen nicht überschreibt.
   */
  const versionsLauf = useRef(0);
  /** Bei einer einzigen Version lädt erst das erste Öffnen; danach bleibt die Liste stehen (kein Abruf je Öffnen). */
  const [versionsOpened, setVersionsOpened] = useState(false);
  if (readSections.versions.offen && !versionsOpened) setVersionsOpened(true);
  const loadVersions = angebot.versionCount >= 2 || versionsOpened;
  useEffect(() => {
    versionsLauf.current += 1;
    const lauf = versionsLauf.current;
    setVersionen(null);
    setVersionView(null);   // das Panel „Ansehen“ gehört zur Liste, die hier neu lädt
    if (!loadVersions) return;
    client.listOfferVersions(kundeId, angebot.id)
      .then(({ items }) => { if (versionsLauf.current === lauf) setVersionen(items); })
      .catch(() => { /* Nebenauskunft — kein Aufhalten des Details */ });
    return () => { versionsLauf.current += 1; };
  }, [client, kundeId, angebot.id, angebot.versionCount, loadVersions]);

  /**
   * Der Ablagestand des aufgeschlagenen Angebots. Nur an einem Angebot, das WIRKLICH
   * hinausgegangen ist — Massstab ist `sentOn`, nicht der Status (Schlussrunde I1): Ein
   * Angebot, das aus dem Entwurf direkt zurückgezogen wurde (DRAFT → WITHDRAWN), hat kein
   * `sentOn` und trotzdem einen Status ungleich DRAFT; ohne diese Prüfung erschiene dort
   * fälschlich „Noch nicht in der Akte abgelegt", obwohl der Server das Ablegen mit 422
   * abweist. Am Entwurf UND an einem nie gesendeten Angebot gibt es keine Ablage, und ein
   * Abruf wäre eine Frage mit bekannter Antwort.
   *
   * Ein Ladefehler bleibt UNBEKANNT (`undefined`) und wird nicht zur Meldung: Der
   * Ablagestand ist eine Randauskunft, und eine Fehlerzeile dafür stünde neben dem
   * Beleg, um den es geht.
   */
  const ablageLesen = useCallback(async (
    id: string, status: OfferStatus, sentOn: string | null,
  ): Promise<void> => {
    if (status === "DRAFT" || sentOn === null) { setAblage(undefined); return; }
    try {
      const { ablage: stand } = await client.getOfferAblage(kundeId, id);
      setAblage(stand);
    } catch {
      setAblage(undefined);
    }
  }, [client, kundeId]);

  useEffect(() => {
    void ablageLesen(angebot.id, angebot.status, angebot.sentOn);
    // `status` UND `sentOn` stehen in den Abhängigkeiten: `status` ändert sich beim
    // Versand (dann gibt es plötzlich eine Ablage), `sentOn` unterscheidet ein gesendetes
    // von einem nie gesendeten, zurückgezogenen Angebot bei GLEICHEM Status (WITHDRAWN).
  }, [angebot.id, angebot.status, angebot.sentOn, ablageLesen]);

  /**
   * Der Übergang „schliesst auf": Beim Öffnen der Bearbeitung wird der Entwurf frisch aus
   * dem Vorgang gebildet, und der Stand des vorigen Anlaufs verfällt.
   *
   * Bewusst WÄHREND des Zeichnens statt in einem Effekt (React: „Adjusting state when a
   * prop changes"): Ein Effekt liefe erst nach dem ersten Bild, und für einen Augenblick
   * stünden die Werte des letzten Anlaufs im Formular. `setPositionen(null)` gehört dazu —
   * sonst spränge der Nettobetrag in die Nur-Lese-Fassung, bevor der Editor geladen hat.
   */
  const [zuletztBearbeitung, setZuletztBearbeitung] = useState(bearbeitung);
  if (bearbeitung !== zuletztBearbeitung) {
    setZuletztBearbeitung(bearbeitung);
    // Der geladene Stand gilt nur für den Ort, an dem er geladen wurde (`useLoadedState`) — in BEIDE Richtungen.
    loaded.reset();
    if (bearbeitung) {
      setEntwurf(angebotEntwurfAus(angebot));
      setReplacesValue(savedReplaces);
      setPruefung(null);
      formular.zuruecksetzen();
      setAnwendenWahl(null);
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      // Und die Faltungen mit: Wer ein Panel neu aufschlägt, findet die Abschnitte im
      // Startzustand vor — nicht so, wie er sie beim vorigen Vorgang hinterlassen hat.
      faltungen.resetAll();
      // Die Vorschau schliesst, wenn die Bearbeitung von AUSSEN beginnt (M5, Schlussrunde):
      // Ihr Schleier liegt bei z-index 60, das Vorschau-Modal bei 100 — ohne diese Zeile
      // läge das Bearbeiten-Panel HINTER der Vorschau, statt sie abzulösen.
      setVorschauOffen(false);
    }
  }

  /**
   * Der Editor meldet nach jedem Schritt die frische Version des VORGANGS und den ganzen
   * Stand. Beides muss ankommen: Die Version geht in das nächste `updateOffer` (sonst 409),
   * und aus dem Stand liest die Maske den berechneten Betrag.
   *
   * Und zwar bis in die LISTE des Rahmens (`onFortschreiben`). Die Liste zu übergehen war
   * ein Fehler (Fund der E2E-Aufgabe 6): Die Statusknöpfe und die Angebot→Auftrag-Naht
   * arbeiten mit der Version AUS DER LISTE. Wer Positionen bearbeitete und das Formular
   * dann mit „Abbrechen" schloss, bekam beim ersten Klick auf „Senden" eine 409 samt der
   * Meldung „Dieses Angebot wurde zwischenzeitlich geändert" — obwohl niemand dazwischenkam.
   */
  function positionenGeaendert(version: number, stand: PositionsStand): void {
    setPositionen(stand);
    p.onFortschreiben({ ...angebot, version, netAmount: stand.netAmount });
    setEntwurf((v) => (
      // Sobald Positionen da sind, ist der Betrag berechnet — das Anzeigefeld führt genau
      // den Wert, den `updateOffer` unverändert zurückerwartet (sonst 422 BETRAG_BERECHNET).
      stand.items.length === 0 ? v : { ...v, netAmount: betragText(stand.netAmount) }));
  }

  /** Hat der Vorgang Positionen? Entschieden an der ANZAHL, nicht am Betrag: Ein Beleg aus
   *  lauter Zugaben zu 0,00 hat Positionen (Fund G1 aus Aufgabe 2). */
  const hatPositionen = positionen !== null && positionen.items.length > 0;

  /**
   * „Vorlage anwenden" am BESTEHENDEN Angebot: Die Positionen der Vorlage werden ANGEHÄNGT,
   * Einleitungs- und Ausleitungstext übernimmt der Server nur, wenn sie leer sind.
   *
   * Danach wird der Kopf FRISCH gelesen. Das ist kein Beiwerk: Der Entwurf im Formular
   * kennt die Texte von vorher. Schickte ihn das nächste Speichern unverändert, machte es
   * die Übernahme des Servers still rückgängig (oder überschriebe einen bestehenden Text
   * mit dem alten Stand) — die Zusage „nur wenn leer" gälte dann nur bis zum nächsten Klick.
   */
  async function vorlageAnwenden(vorlageId: string): Promise<void> {
    if (vorlageId === "") return; // der Knopf ist ohne Wahl gesperrt
    setAnwendenVersucht(true);
    weg.current = async () => {
      try {
        const stand = await client.applyTemplate(
          kundeId, angebot.id, angebot.version, vorlageId);
        // Der Editor führt seinen Stand selbst — der Zähler im `key` hängt ihn neu auf,
        // damit er die angehängten Zeilen lädt. `positionenGeaendert` allein reichte nicht.
        setPositionenNeuLaden((n) => n + 1);
        setPositionen(stand);
        const frisch = await client.getOffer(kundeId, angebot.id);
        // Und in die LISTE, geradeaus statt über Umwege: Die Statusknöpfe und die
        // Angebot→Auftrag-Naht rechnen mit der Version aus der Liste.
        p.onFortschreiben(frisch);
        setEntwurf(angebotEntwurfAus(frisch));
        return true;
      } catch (e) {
        // Eine 409 gehört der MASKE (fremder Stand mit „Neu laden“) — der Subdialog geht
        // zu, sonst stünde er über der Hinweiszeile, die jetzt gilt. Andere Fehlschläge
        // bleiben im Subdialog stehen (dort wurde „Anwenden“ gedrückt).
        if (e instanceof ApiError && e.status === 409) setAnwendenWahl(null);
        // Ein 422-Befund an einem Feld der MASKE (Fixrunde 1, Review-Hinweis 2): Er steht am
        // Feld dahinter, und der Sprung zum ersten Fehler zielt dorthin — der Subdialog geht
        // im selben Zug zu, sonst bliebe er leer über einer Maske, die man nicht bedienen kann.
        if (e instanceof ApiError && e.status === 422
            && (e.fields ?? []).some((f) => ANGEBOT_FELDER_AM_FELD.includes(f.field))) {
          setAnwendenWahl(null);
        }
        // Auch der SERVER kann Kopffelder bemängeln (das Anwenden schreibt den Kopf mit).
        openSectionsWithFindings(faltungen, ANGEBOT_ABSCHNITTSFELDER, e);
        throw e;
      }
    };
    if (await formular.aktion() !== true) return;
    setAnwendenWahl(null);
    p.onHinweis("Die Vorlage ist angewandt — ihre Positionen stehen am Ende der Liste.");
  }

  /** Der Subdialog „Vorlage anwenden“ — Abbrechen mit Rückfrage, sobald eine Vorlage gewählt
   *  ist (Hausregel striktes Modal); während des Anwendens gesperrt. */
  async function anwendenAbbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(anwendenWahl !== null && anwendenWahl !== ""))) return;
    setAnwendenWahl(null);
  }

  /** Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
   *  `speichereParteien` (vorgangsdetail.ts), damit alle drei Beleg-Masken EINE Naht
   *  haben. `null` heisst: Der Kopf steht, die Parteien nicht; das Panel bleibt offen.
   *  Sonst die Version, die der Beleg NACH diesem Weg trägt — der DRITTE Weg
   *  (Zahlplan) rechnet mit ihr weiter. */
  function parteienSpeichern(
    frisch: Angebot, folgefehler: { e: unknown },
  ): Promise<number | null> {
    let version: number | null = null;
    return speichereParteien({
      client, art: "offer", customerId: angebot.customerId, vorgangId: angebot.id,
      version: frisch.version, griff: parteien,
      beiFehler: (meldung, fehler) => {
        // Der frische Vorgang muss in die Liste des Rahmens und in den Entwurf — sonst
        // rechnete der nächste Anlauf mit der alten Version und bekäme eine 409 über eine
        // Änderung, die niemand fremd gemacht hat.
        p.onFortschreiben(frisch);
        setEntwurf(angebotEntwurfAus(frisch));
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAngebotsFehlerFeld(faltungen, BEARBEITEN_PRAEFIX, fehler);
      },
      beiErfolg: (neueVersion) => { version = neueVersion; },
    }).then((ok) => (ok ? version ?? frisch.version : null));
  }

  /** Der DRITTE Speicherweg — die Zahlungsvereinbarung. Reihenfolge, Version und Sinn
   *  stehen an `speichereZahlplan` (vorgangsdetail.ts): Kopf → Parteien → Zahlplan, und
   *  die Version ist die aus der ANTWORT des Parteien-Weges. */
  function zahlplanSpeichern(
    vorgangId: string, version: number, folgefehler: { e: unknown },
  ): Promise<boolean> {
    return speichereZahlplan({
      client, bezug: { art: "offer", customerId: angebot.customerId, vorgangId },
      version, griff: zahlplan,
      beiFehler: (meldung, fehler) => {
        folgefehler.e = folgewegFehler(meldung, fehler);
        zeigeAngebotsFehlerFeld(faltungen, BEARBEITEN_PRAEFIX, fehler);
      },
    });
  }

  async function speichern(): Promise<void> {
    const problem = pruefeAngebot(entwurf, false);
    if (problem !== null) {
      setPruefung(problem);
      // Der Befund betrifft immer ein Feld der Kopfdaten — die beim Bearbeiten zugeklappt
      // beginnen. Ohne dieses Aufklappen stünde die Meldung über einem Formular, in dem das
      // gemeinte Feld gar nicht zu sehen ist: ein unsichtbarer Fehler.
      zeigeAngebotsFeld(faltungen, BEARBEITEN_PRAEFIX, problem.feld);
      return;
    }
    setPruefung(null);
    weg.current = kopfSpeichern;
    if (await formular.aktion() !== true) return;
    p.onSchliessen();
    await p.onNeuLaden();
  }

  /**
   * Der Speicherweg im Formularzustand: Kopf → Parteien → Zahlplan. Jeder Weg dreht die
   * Belegversion weiter; der nächste bekommt die aus der ANTWORT des vorigen — der Zahlplan
   * steht zuletzt, weil er der einzige Weg ist, der die geplanten Fälligkeiten neu rechnet.
   *
   * version = die gelesene Version: ein zwischenzeitlich fremd geänderter Satz ergibt 409
   * statt einer stillen Überschreibung. BINDEND FÜR VERWENDER: Die Liste darf nicht neu
   * laden, solange ein Detail in Bearbeitung ist — die Version, die hier hinausgeht, ist die
   * aus der Liste des Rahmens (`angebot`); ein Neuladen während der Bearbeitung ersetzte sie
   * unbemerkt durch die fremde und machte die optimistische Sperre wirkungslos.
   */
  async function kopfSpeichern(): Promise<boolean> {
    // Zuerst die Nachfolge, und nur bei Änderung: Sie dreht die Belegversion nicht — ein 422 (ERSATZ_RING …) steht so am
    // Feld, bevor irgendetwas anderes geschrieben ist. Der neue Stand geht SOFORT an den Rahmen (wie bei
    // `parteienSpeichern`): Scheitert danach der Kopf, liegt der Verweis schon auf dem Server — Chips und gespeicherter
    // Wert müssen ihn zeigen, sonst bliebe er beim Verwerfen unbemerkt stehen.
    if (replacesValue !== savedReplaces) {
      try {
        p.onFortschreiben(await client.setOfferReplaces(kundeId, angebot.id, replacesValue));
      } catch (e) {
        openSectionsWithFindings(faltungen, ANGEBOT_ABSCHNITTSFELDER, e);
        throw e;
      }
    }
    let frisch: Angebot;
    try {
      frisch = await client.updateOffer(
        kundeId, angebot.id, angebot.version, angebotKopffelder(entwurf));
    } catch (e) {
      // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske
      // (Titel-Höchstlänge, Betragsgrenzen, Halbe-Kündigungsfrist, BETRAG_BERECHNET …).
      // Steht das Feld in einem zugeklappten Abschnitt, geht er im SELBEN Zug auf, in dem der
      // Befund ans Feld kommt — erst dann findet der Sprung zum ersten Fehler das Feld.
      openSectionsWithFindings(faltungen, ANGEBOT_ABSCHNITTSFELDER, e);
      throw e;
    }
    const folgefehler: { e: unknown } = { e: null };
    const parteienVersion = await parteienSpeichern(frisch, folgefehler);
    if (parteienVersion === null) throw folgefehler.e;
    if (!await zahlplanSpeichern(angebot.id, parteienVersion, folgefehler)) {
      throw folgefehler.e;
    }
    return true;
  }

  async function statusSetzen(status: OfferStatusTarget): Promise<void> {
    // Erst die Rückfrage (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026): Ein Statuswechsel hat kein
    // Rückgängig — Angenommen, Abgelehnt und Zurückgezogen sind Endzustände, Gesendet legt Layout und Anlagen fest.
    if (!(await bestaetigen(STATUS_CHANGE_TEXT.offer[status](angebot.displayNumber)))) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    // Wie an `alsPdf`/`inAkteAblegen`: eine ALTE PDF-Quittung ("wurde heruntergeladen …")
    // gehört zu einem vorigen Klick und nicht zu diesem Statuswechsel — sie bliebe sonst
    // stehen, bis (falls überhaupt) eine neue Meldung sie ersetzt (M6, Schlussrunde).
    setPdfMeldung(null);
    try {
      const neu = await client.setOfferStatus(kundeId, angebot.id, angebot.version, status);
      // Die Ablage-Auskunft des Statuswechsels (Spec Belegvorschau): Beim Versand legt
      // der Server das endgültige PDF in die Kundenakte. Gelingt es, steht der Stand
      // hier; scheitert es, ist der Status TROTZDEM gesetzt, und `ablageHinweis` sagt,
      // wie es nachzuholen ist. Beides wird in DIESELBE Meldungsstelle geschrieben wie
      // die PDF-Wege — eine zweite Zeile für dieselbe Sorte Auskunft wäre eine Stelle,
      // die man beim nächsten Fix vergisst.
      //
      // `neu.ablage === null` ist zweideutig (M1, Schlussrunde): An ACCEPTED/WITHDRAWN
      // versucht der Server gar nicht erst abzulegen (`ablageNachVersand` legt NUR bei
      // SENT ab) — dort heisst `null` "nichts versucht", nicht "nichts da", und eine
      // schon geladene Ablage darf stehen bleiben. Nur am WIRKLICHEN Versand (`status
      // === "SENT"`) ist `null` eine echte Auskunft ("versucht, nichts da") und wird
      // übernommen.
      if (neu.ablage !== null || status === "SENT") setAblage(neu.ablage);
      if (neu.ablageHinweis !== null) {
        setPdfMeldung({ text: neu.ablageHinweis, art: "schlecht" });
      } else if (neu.ablage !== null) {
        setPdfMeldung({
          text: `Das PDF liegt als „${neu.ablage.fileName}" in der Kundenakte.`,
          art: "gut",
        });
      }
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieses Angebot wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen.");
        await p.onNeuLaden();
      } else {
        p.onHinweis(apiMeldung(e, "Der Status konnte nicht geändert werden."));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „PDF in Akte ablegen" — das Nachholen, wenn die Automatik beim Versand nicht kam
   * (oder das Erneuern nach einer Änderung).
   *
   * IDEMPOTENT: Liegt die Datei schon, wird ihr Inhalt ersetzt — dieselbe Kennung, kein
   * zweiter Eintrag, und ein vom Bearbeiter geänderter Name bleibt stehen.
   */
  async function inAkteAblegen(): Promise<void> {
    // Erneuern ersetzt den Inhalt der abgelegten Datei — erst die Rückfrage (Entscheid 683); das erste Ablegen fragt nicht.
    if (ablage !== null && ablage !== undefined
      && !(await bestaetigen(RENEW_FILED_PDF_QUESTION(ablage.fileName)))) return;
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const stand = await client.createOfferAblage(kundeId, angebot.id);
      setAblage(stand);
      // Derselbe Wortlaut wie am „PDF in Akte ablegen"-Knopf der Rechnung
      // (`RechnungDetail.inAkteAblegen`) — beide Belegarten sprechen hier bewusst dieselbe
      // Sprache. Die flüchtige Meldung und die DAUERHAFTE Ablage-Zeile unter den Aktionen
      // sagen zwar dasselbe, aber mit unterschiedlichem Wortlaut ("liegt JETZT" vs.
      // "ABGELEGT am") — genau deshalb lässt sich in Tests jede der beiden gezielt
      // ansprechen, ohne dass eine Suche nach dem Dateinamen beide träfe.
      setPdfMeldung({
        text: `Das PDF liegt jetzt in der Akte (${stand.fileName}) — im Reiter „Dateien".`,
        art: "gut",
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        setPdfMeldung({
          text: apiMeldung(e, "Das PDF konnte nicht in der Kundenakte abgelegt werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" (E3-T4): Die Engine setzt das Angebot (Dokumentart ANGEBOT), die API
   * liefert den Download durch, hier wird er gespeichert. Die drei Ausgänge tragen ihre
   * ART mit (PDF_MELDUNGS_STIL): Erfolg als Status, die 501 der unkonfigurierten
   * Umgebung als HINWEIS (bekannter Arbeitsstand, kein Alarm), alles andere als Alarm.
   */
  async function alsPdf(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, angebot.id);
      const name = filename ?? "angebot.pdf";
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`, an JEDEM
        // Engine-Fehlschlag) und werden unten als Liste gezeigt. Der Schlüssel entfällt,
        // wenn keine kamen — `exactOptionalPropertyTypes`.
        setPdfMeldung({
          text: apiMeldung(e, "Das Angebot konnte nicht als PDF ausgegeben werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  const { bestaetigen, bestaetigenMitText } = useBestaetigung();

  /**
   * „Neue Version": eine Rückfrage mit dem freiwilligen Feld „Anlass“ (477 b; die Frage IST die Bestätigung, ein
   * leeres Feld geht als `null` hinaus), dann der Aufruf — und dann meldet das Detail dem
   * RAHMEN die NEUE Kennung (`onErsetzt`), statt sie hier weiterzuführen oder den Rahmen
   * bloss zum Neuladen zu bitten.
   *
   * Review-Befund Fixrunde 1: Ein `onNeuLaden` allein liefe mit der ALTEN Kennung —
   * `onNeuLaden` liest genau den Vorgang, den der Rahmen gerade führt, und das ist noch
   * der abgelöste. Es läse also die jetzt SUPERSEDED Fassung zurück und überschriebe die
   * eben angelegte neue Version sofort wieder. `onErsetzt` gibt dem Rahmen deshalb den
   * NEUEN Satz direkt mit — er muss ihn nicht ein zweites Mal vom Server holen — und mit
   * ihm auch dessen Kennung, damit der Rahmen seinen Bezug (und, wo es eine Adresse gibt,
   * sie selbst) umstellt und seine Liste nachzieht.
   */
  async function neueVersion(vorlageOfferId: string | null = null): Promise<void> {
    const antwort = await bestaetigenMitText({
      ...(vorlageOfferId === null
        ? { titel: SUCCESSION_TEXT.newVersionTitle, text: SUCCESSION_TEXT.newVersionText,
          bestaetigenText: SUCCESSION_TEXT.newVersion }
        : { titel: SUCCESSION_TEXT.restoreTitle, text: SUCCESSION_TEXT.restoreText,
          bestaetigenText: SUCCESSION_TEXT.restoreConfirm }),
      begruendungLabel: SUCCESSION_TEXT.versionNoteLabel,
      // Der Server nimmt höchstens 500 Zeichen (477 b) — das Feld sagt es vorher (Review 28 K1).
      maxLaenge: VERSION_NOTE_MAX_LENGTH,
    });
    if (!antwort.ok) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const neu = await client.neueOfferVersion(kundeId, angebot.id, vorlageOfferId,
        antwort.text === "" ? null : antwort.text);
      p.onHinweis(`Version ${neu.versionNumber} wurde angelegt. Die bisherige Fassung `
        + "ist jetzt veraltet.");
      p.onErsetzt(neu);
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Die neue Version konnte nicht angelegt werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" AN EINER BESTIMMTEN VERSION — der Weg aus dem Versionsabschnitt. Er ruft
   * denselben Rendern-Weg wie `alsPdf`, nur mit der Kennung der gewählten Version, und
   * meldet in dieselbe Rückmeldung (`pdfMeldung`). Eine zweite Meldungsstelle je Zeile
   * wäre ein zweiter Ort für dieselbe Auskunft.
   */
  async function alsPdfVon(v: AngebotVersion): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOffer(kundeId, v.id);
      const name = filename ?? `angebot-v${String(v.versionNumber)}.pdf`;
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      setPdfMeldung({
        text: apiMeldung(e, "Diese Version konnte nicht als PDF ausgegeben werden."),
        art: e instanceof ApiError && e.status === 501 ? "hinweis" : "schlecht",
      });
    } finally {
      setBeschaeftigt(false);
    }
  }

  /** „Kopieren“: dieselbe Bauart, anderes Ergebnis — ein Angebot mit EIGENER Nummer und eigener Kennung. Gefragt wird
   *  vorher in `CopyOfferDialog` (beide Wege erklärt, Schalter „ersetzt ‹Nr›“). Die Meldung nennt die neue Nummer, weil
   *  genau sie der Unterschied zur neuen Version ist. Wie bei `neueVersion` meldet das Detail dem Rahmen die neue
   *  Kennung über `onErsetzt` — ohne das bliebe die Kopie unsichtbar: Sie steht unter einer ANDEREN Kennung als der
   *  gerade gezeigte Vorgang, und ein blosses „bitte neu laden" läse weiterhin nur diesen. */
  async function copyOffer(replacesSource: boolean): Promise<void> {
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      const kopie = await client.kopiereOffer(kundeId, angebot.id, replacesSource);
      p.onHinweis(SUCCESSION_TEXT.copied(kopie.displayNumber));
      setCopyDialogOpen(false);
      p.onErsetzt(kopie);
    } catch (e) {
      p.onHinweis(apiMeldung(e, SUCCESSION_TEXT.copyFailed));
      setCopyDialogOpen(false);
    } finally {
      setBeschaeftigt(false);
    }
  }

  /** Ein Nachfolge-Chip springt zur Reihe (`jumpToSeries`): in der Akte zur Karte, im Bereich über `onErsetzt`. */
  async function openSeries(offerId: string): Promise<void> {
    if (gesperrt || bearbeitung) return;
    await jumpToSeries(angebot, offerId, p);
  }

  /**
   * Trägt das Panel ungespeicherte Eingaben? Der Kopfblock PARTEIEN zählt mit: Er meldet
   * seinen Entwurf erst, sobald ihn jemand angefasst hat — und genau dann gibt es etwas zu
   * verlieren.
   */
  const ungespeichert = bearbeitung
    && (!entwuerfeGleich(entwurf, angebotEntwurfAus(angebot))
      || replacesValue !== savedReplaces || parteien.beruehrt || zahlplan.beruehrt);

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  /** Die Sendesperre (Dirk 476, F11): Eine ersetzte Reihe wird nicht mehr versandt — kein „Als gesendet markieren“,
   *  stattdessen der Hinweis mit dem ersten Nachfolger (derselbe, den der Server in seiner 409 nennt). Zurückziehen und
   *  Ablehnen bleiben, damit sie sauber geschlossen werden kann. */
  const successor = angebot.replaced ? (angebot.replacedBy[0] ?? null) : null;
  const folgestatus = erlaubteFolgestatus(angebot.status).filter((s) => successor === null || s !== "SENT");
  const kuerzel = companies.find((c) => c.id === angebot.companyId)?.shortCode ?? null;

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei
   * ungespeicherten Kopfeingaben. Knopf und Escape gehen durch diese Funktion.
   *
   * Verglichen wird gegen den Vorgang, wie er GERADE steht — nicht gegen einen zweiten,
   * mitgeführten Ausgangsstand: Ein Schritt im Positions-Editor dreht die Version weiter und
   * schreibt den Betrag ins Feld zurück (`positionenGeaendert`), und der Vorgang kommt über
   * `onFortschreiben` frisch wieder herein. Ein eingefrorener Ausgangsstand meldete danach
   * eine „Änderung", die längst gespeichert ist.
   *
   * Die POSITIONEN selbst stehen nie ungespeichert im Panel — der Editor schreibt jeden
   * Schritt sofort. Die Rückfrage kann sie deshalb übergehen, ohne etwas zu verschweigen.
   */
  async function verlassen(): Promise<boolean> {
    if (speichert) return false;
    if (!(await darfAbbrechen(ungespeichert))) return false;
    parteien.zuruecksetzen();
    zahlplan.zuruecksetzen();
    formular.zuruecksetzen();
    p.onSchliessen();
    return true;
  }
  async function abbrechen(): Promise<void> { await verlassen(); }
  /** „Ordner“ im Panel geht durch denselben Ausgang (Fix-Runde 1 zu Aufgabe 25), erst dann in den Reiter Dateien. */
  const folderFromPanel = useJumpAfterLeaving(bearbeitung, verlassen, p.onOrdnerOeffnen);

  /** Der Text der Hinweiszeile „fremder Stand“ (Slot `hinweis` des Formulars): wer wann
   *  geändert hat, sofern der Server es nennt, und was „Neu laden“ tut — die Eingaben
   *  stehen bis dahin lesbar da (nichts geht still verloren). */
  function fremderStandHinweis(): ReactNode {
    if (formular.zustand !== "fremder-stand") return undefined;
    const wer = formular.fremderStand?.changedBy;
    return (
      <>
        {wer !== undefined && wer !== ""
          ? `${wer} hat dieses Angebot inzwischen geändert.`
          : "Dieses Angebot wurde inzwischen geändert."}
        {" Deine Eingaben stehen noch da — „Neu laden“ holt den aktuellen Stand und "
          + "ersetzt sie."}
      </>
    );
  }

  /** Befunde je Feld: die des Servers und die der Maskenprüfung. */
  const fehlerJeFeld: Record<string, string> = {
    ...formular.feldFehler,
    ...(pruefung !== null ? { [pruefung.feld]: pruefung.meldung } : {}),
    // Die Befunde der beiden Folgewege stehen in IHREN Blöcken — für den Fehlerzähler
    // ihres Abschnitts zählen sie mit.
    ...parteien.befunde, ...zahlplan.befunde,
  };


  return (
    <>
      {/* Die KARTE des Vorgangs — `display: contents`, sie ordnet nichts an. An einer ersetzten Reihe tritt sie zurück
          (`kang__card--replaced`, AngebotDetail.css): alles außer den Nachfolge-Chips gedimmt. Panel, Kopierrückfrage
          und Vorschau stehen bewusst AUSSERHALB — Ebenen über der Karte dimmen nicht mit. */}
      <div className={`kang__card${angebot.replaced ? " kang__card--replaced" : ""}`}>
        <div className="kang__zeile" {...(angebot.istJuengste ? { id: seriesAnchorId(angebot.seriesId), tabIndex: -1 } : {})}>
          <span className="kang__nummer">{angebot.displayNumber}</span>
          {/* Die Versionspille steht NEBEN der Nummer, nicht IN ihr (Festlegung V4):
              Die Nummer bleibt die Nummer, auch beim Kopieren in die Zwischenablage. */}
          {versionsZusatz(angebot.versionNumber) !== "" && (
            <Chip className="kang__version">{versionsZusatz(angebot.versionNumber)}</Chip>
          )}
          {kuerzel !== null && <Chip {...hausfarbeChip(companies, kuerzel)}>{kuerzel}</Chip>}
          <span className="kang__eintrag-titel">{angebot.title}</span>
          {/* Kennzeichen wie in der Bereichsliste (ANGEBOT_STATUS_ART, Entscheid 671). */}
          <Chip className="kang__status" statusArt={ANGEBOT_STATUS_ART[angebot.status]}>{ANGEBOT_STATUS_LABEL[angebot.status]}</Chip>
          {/* Die Nachfolge (Aufgabe 27): Chips MIT Rahmen, ein Klick springt zur Reihe; sie bleiben an einer ersetzten
              Reihe ungedimmt. „kopiert aus“ ist eine leise Auskunft, kein Bedienelement. */}
          <ReplacesChips replaces={angebot.replaces} replacedBy={angebot.replacedBy} disabled={gesperrt || beschaeftigt}
            onOeffnen={(offerId) => { void openSeries(offerId); }} />
          {angebot.copiedFrom !== null && (
            <span className="kang__copied-from">
              {SUCCESSION_TEXT.copiedFrom(angebot.copiedFrom.displayNumber, angebot.copiedFrom.versionNumber)}
            </span>
          )}
        </div>

        {/* Die abgelöste Version sagt es SELBST und nennt, wodurch: Ohne diesen Satz
            stünde ein Bearbeiter vor einem Angebot ohne Bearbeiten-Knopf und wüsste
            nicht, warum. Die Ordnungszahl kommt aus dem geladenen Versionsabschnitt
            (`versionen[0]` — jüngste zuerst): `angebot.versionCount` ist der Stand BEIM
            LESEN dieser (abgelösten) Zeile und kann inzwischen veraltet sein, wenn seither
            eine weitere Version entstand. Solange die Liste noch lädt, bleibt `versionCount`
            der einzig verfügbare Rückfall (Fixrunde nach Gesamtreview, M1). */}
        {!angebot.istJuengste && (
          <Hinweis art="warnung">
            Veraltet, ersetzt durch {versionsZusatz(versionen?.[0]?.versionNumber
              ?? angebot.versionCount)}
          </Hinweis>
        )}

        <div className="kang__daten">
          <span className="kang__betrag ki-num">
            {formatBetrag(angebot.netAmount, angebot.currency)}
          </span>
          {angebot.validUntil !== null && (
            <span className="kang__gueltig">
              gültig bis {formatDatum(angebot.validUntil)}
            </span>
          )}
          {angebot.contactName !== null && angebot.contactName !== "" && (
            <span className="kang__kontakt">{angebot.contactName}</span>
          )}
        </div>

        {/* Die PDF-Rückmeldung steht AM VORGANG, zu dem sie gehört: Der Baustein trägt sie
            samt ihrer Befundliste selbst, damit sie auch dort erscheint, wo kein Reiter
            darüber liegt (der Angebots-Bereich aus Aufgabe 3). */}
        {pdfMeldung !== null && (
          <Hinweis art={PDF_HINWEIS_ART[pdfMeldung.art]}>{pdfMeldung.text}</Hinweis>
        )}
        {/* Die Feldbefunde als LISTE — jeder Pfad einzeln und ungekürzt, zum Abhaken.
            NEBEN der Meldung und nicht in ihr: Die trägt `role="alert"` und muss ein `<p>`
            bleiben (ein `<ul>` darf dort gar nicht hinein), und die Ansage soll ein SATZ
            sein, keine vorgelesene Liste.

            EIGENER Blockname `kang__` und kein geteilter Block mit den Rechnungen: Dieselbe
            Klasse über zwei Ansichten hinweg hiesse, dass eine Stilanpassung an der einen
            die andere still mitverändert. Die Gestalt ist bewusst dieselbe, der Name nicht. */}
        {pdfMeldung?.felder !== undefined && pdfMeldung.felder.length > 0 && (
          <ul className="kang__fehlerfelder" aria-label="Fehlende Pflichtwerte">
            {pdfMeldung.felder.map((f) => (
              <li key={f.field}>
                <code>{f.field}</code>: {f.message}
              </li>
            ))}
          </ul>
        )}

        {/* Die Abschnitte des geschlossenen Details in der Folge der Spec-Tabelle: Zahlungsvereinbarung,
            Anlagen aus Textbausteinen, Dateien (hier bedienbar, lädt erst beim Öffnen), Versionen — alle zu, Versionen offen
            ab zwei Versionen. Zahlungsvereinbarung
            und Anlagen hängen ihren Inhalt erst beim Öffnen ein (Gesamtreview, I2): `Zahlplan` und `Anhaenge` rufen in
            ihrem Effekt sofort ab, sobald sie gezeichnet werden — bei vielen Angeboten in der Liste wäre das ein Abruf
            je Zeile, den fast niemand ansieht. Gepflegt wird beides im Modal, hier steht nur das Ergebnis. */}
        {!bearbeitung && (
          <>
            {/* Nach dem ersten Laden sagen beide Köpfe zugeklappt ihre Zahl (Spec-Spalte „Zugeklappt sichtbar“). */}
            <Vorgangsabschnitt art="abrechnung" titel={SECTION_TEXT.payment} faltung={readSections.payment}
              inhaltId={`kang-zahlplan-inhalt-${angebot.id}`} {...summaryProp(ratesSummary(loaded.rateCount))}>
              {readSections.payment.offen && (
                <Zahlplan client={client}
                  bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                  idPraefix={`kang-lese-${angebot.id}`} nurLesend onLoaded={loaded.onRatesLoaded} />
              )}
            </Vorgangsabschnitt>
            {apClient !== null && <Vorgangsabschnitt art="kunde" titel={SECTION_TEXT.representatives}
              faltung={readSections.representatives} inhaltId={`kang-ansprechpartner-inhalt-${angebot.id}`}
              {...counterProp(loaded.representativeCount)}>
              {representativesBox(readSections.representatives.offen, `kang-lese-${angebot.id}`)}
            </Vorgangsabschnitt>}
            <Vorgangsabschnitt art="textbaustein" titel={SECTION_TEXT.textAttachments}
              faltung={readSections.textAttachments} inhaltId={`kang-anhaenge-inhalt-${angebot.id}`}
              {...counterProp(loaded.attachmentCount)}>
              {readSections.textAttachments.offen && (
                <Anhaenge client={client}
                  bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                  idPraefix={`kang-lese-${angebot.id}`} nurLesend onLoaded={loaded.onAttachmentsLoaded} />
              )}
            </Vorgangsabschnitt>
            <FilesReadSection faltung={readSections.files} inhaltId={`kang-dateien-inhalt-${angebot.id}`}
              content={filesContent(files, readSections.files.offen, loaded)} />
            <Vorgangsabschnitt art="version" titel={SECTION_TEXT.versions} faltung={readSections.versions}
              inhaltId={`kang-versionen-inhalt-${angebot.id}`} zaehler={versionen?.length ?? angebot.versionCount}>
              {/* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Kopfzeile, Zebra und das
                  waagerechte Rollen bringt das Element; der Betrag steht rechtsbündig. */}
              {versionen !== null && (
                <Tabelle stapeln dicht className="kang__versionen-tabelle">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Version</th>
                      <th scope="col">Datum</th>
                      <th scope="col">Status</th>
                      <th scope="col" data-ausrichtung="rechts">Betrag</th>
                      <th scope="col">{SUCCESSION_TEXT.versionNoteLabel}</th>
                      <th scope="col">Aktionen</th>
                    </tr>
                  </thead>
                  {/* JÜNGSTE OBEN (Hausregel „Wichtigstes oben") — der Server liefert sie
                      bereits in dieser Reihenfolge; hier wird NICHT nachsortiert. */}
                  <tbody>
                    {versionen.map((v) => (
                      <tr key={v.id}
                        className={v.id === angebot.id ? "kang__versionen-zeile--aktiv" : undefined}
                        aria-current={v.id === angebot.id ? "true" : undefined}>
                        <th scope="row">{versionsZusatz(v.versionNumber) || "v1"}</th>
                        <td>{formatDatum(v.createdOn)}</td>
                        <td>{ANGEBOT_STATUS_LABEL[v.status]}</td>
                        <td className="ki-num" data-ausrichtung="rechts">
                          {formatBetrag(v.netAmount, angebot.currency)}
                        </td>
                        <td className="kang__versionen-anlass"><VersionNote text={v.versionNote ?? ""} /></td>
                        <td>
                          {/* Eine echte Tabellenzelle; die Knöpfe stehen klein in einer Zeile mit Umbruch
                              (Sichtprobe Task 29: die Aktionen ragten aus der Tabelle). */}
                          <span className="kang__versionen-aktionen">
                          {/* JEDE Version bleibt als PDF reproduzierbar — das ist der Kern
                              der Spec („Jede Version ist vollständig lesbar und als PDF
                              reproduzierbar, auch nachdem eine neuere entstanden ist"). Der
                              Rendern-Weg nimmt die Kennung DIESER Version, nicht die des
                              gezeigten Angebots. */}
                          <KiKnopf kompakt name={`Version ${v.versionNumber} als PDF`}
                            deaktiviert={gesperrt || beschaeftigt}
                            onClick={() => { void alsPdfVon(v); }}>
                            Als PDF
                          </KiKnopf>
                          {/* „Ansehen“ (Dirk 484): die Seitenbilder einer ÄLTEREN Version lesend im seitlichen Panel.
                              An der gezeigten Version ist es die „Vorschau“ unten — kein zweiter Knopf dafür. */}
                          {v.id !== angebot.id && (
                            <KiKnopf kompakt name={SUCCESSION_TEXT.viewActionLabel(v.versionNumber)}
                              deaktiviert={gesperrt || beschaeftigt}
                              onClick={() => setVersionView(v)}>
                              {SUCCESSION_TEXT.viewAction}
                            </KiKnopf>
                          )}
                          {/* „Version wiederherstellen" ist keine eigene Mechanik, sondern
                              eine neue Version MIT DIESER als Vorlage — deshalb steht der
                              Knopf hier und heisst, was er tut. An der gezeigten (jüngsten)
                              Version selbst wäre er der gewöhnliche „Neue Version"-Knopf und
                              deshalb weggelassen. */}
                          {angebot.darfNeueVersion && v.id !== angebot.id && (
                            <KiKnopf kompakt name={`Version ${v.versionNumber} als Vorlage für neue Version`}
                              deaktiviert={gesperrt || beschaeftigt}
                              onClick={() => { void neueVersion(v.id); }}>
                              Als Vorlage für neue Version
                            </KiKnopf>
                          )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </Tabelle>
              )}
            </Vorgangsabschnitt>
          </>
        )}

        {!bearbeitung && (
          <div className="kang__aktionen">
            {/* Bearbeiten NUR an der jüngsten Version. Eine ältere ist lesend — der
                Server wiese sie mit 422 ANGEBOT_VERALTET ab, und ein Knopf, der das
                sicher auslöst, ist keine Bedienführung, sondern eine Falle. */}
            {angebot.istJuengste && (
              <KiKnopf name={`${angebot.title} bearbeiten`}
                deaktiviert={gesperrt || beschaeftigt} onClick={p.onBearbeiten}>
                Bearbeiten
              </KiKnopf>
            )}
            {/* Die Vorschau — DIREKT NEBEN „Als PDF" (Spec, wörtlich): Beide zeigen dasselbe
                Druckbild, einmal auf dem Bildschirm, einmal als Datei. */}
            <KiKnopf deaktiviert={gesperrt || beschaeftigt}
              onClick={() => setVorschauOffen(true)}>
              Vorschau
            </KiKnopf>
            <KiKnopf deaktiviert={gesperrt || beschaeftigt} onClick={() => { void alsPdf(); }}>
              Als PDF
            </KiKnopf>
            {/* Das LAYOUT des Belegs (Migration 049, Frage 263) — neben Vorschau und PDF, deren Blatt es
                bestimmt. Fest ab dem Versand; der Knopf zeigt es danach lesend. */}
            <BelegLayoutKnopf client={client} art="offer" customerId={angebot.customerId}
              vorgangId={angebot.id} gesperrt={gesperrt || beschaeftigt} version={angebot.version}
              onNeuLaden={p.onNeuLaden}
              onVersion={(version) => p.onFortschreiben({ ...angebot, version })} />
            {folgestatus.map((s) => (
              <KiKnopf key={s} deaktiviert={gesperrt || beschaeftigt}
                onClick={() => { void statusSetzen(s); }}>
                {STATUS_AKTION[s]}
              </KiKnopf>
            ))}
            {/* Der Satz steht NEBEN dem Knopf und nicht in ihm: Die Beschriftung soll
                kurz bleiben, die Ansage ausdrücklich sein. */}
            {folgestatus.includes("SENT") && (
              <span className="kang__statushinweis">{SENT_HINWEIS}</span>
            )}
            {/* „stattdessen“ (Spec): nur dort, wo sonst „Als gesendet markieren“ stünde. */}
            {successor !== null && erlaubteFolgestatus(angebot.status).includes("SENT") && (
              <span className="kang__statushinweis kang__statushinweis--replaced">
                {SUCCESSION_TEXT.replacedHint(successor.displayNumber)}
              </span>
            )}
            {/* Die Ablage in der Kundenakte NACHHOLEN oder ERNEUERN — sichtbar, sobald wir
                eine Antwort auf „liegt sie?" haben (`ablage !== undefined`). Das ist bewusst
                NICHT an `angebot.status` gebunden: Direkt nach „Als gesendet markieren" hat
                der Rahmen die frische Version des Angebots (noch) nicht nachgeladen, der
                Ablagestand aus der STATUSANTWORT ist aber schon da — und genau dann soll der
                Knopf (bzw. die Auskunft, dass es NICHT geklappt hat) sofort stehen, nicht erst
                nach einem Neuladen. Am Entwurf UND an einem nie gesendeten Angebot bleibt
                `ablage` auf `undefined` (siehe `ablageLesen`), der Knopf fehlt also dort
                ganz — ein Klick würde der Server mit 422 ABLAGE_UNZULAESSIG abweisen, und
                das wäre keine Bedienführung, sondern eine Falle (dieselbe Regel wie am
                „Bearbeiten" einer alten Version). Die zusätzliche Prüfung auf
                `angebot.sentOn` (Schlussrunde I1) fängt den Wimpernschlag ab, in dem beim
                Wechsel des aufgeschlagenen Angebots der ALTE `ablage`-Stand noch steht,
                während `angebot` schon das NEUE (nie gesendete) zeigt. */}
            {ablage !== undefined && angebot.sentOn !== null && (
              <KiKnopf deaktiviert={gesperrt || beschaeftigt}
                onClick={() => { void inAkteAblegen(); }}>
                {/* Wie an der Rechnung: „ablegen" ist das erste Mal, „erneuern" sagt, dass
                    schon etwas liegt und ersetzt wird (IDEMPOTENT, siehe `inAkteAblegen`). */}
                {ablage === null ? "PDF in Akte ablegen" : "PDF in der Akte erneuern"}
              </KiKnopf>
            )}
            {angebot.darfNeueVersion && (
              <KiKnopf deaktiviert={gesperrt || beschaeftigt}
                onClick={() => { void neueVersion(null); }}>
                {SUCCESSION_TEXT.newVersion}
              </KiKnopf>
            )}
            {/* Kopieren geht IMMER — aus jedem Status, auch aus einer veralteten Version
                und auch nach abgeleitetem Auftrag (Spec). */}
            <KiKnopf deaktiviert={gesperrt || beschaeftigt} onClick={() => setCopyDialogOpen(true)}>
              {SUCCESSION_TEXT.copyAction}
            </KiKnopf>
            {angebot.status === "ACCEPTED" && (
              <KiKnopf art="primaer" deaktiviert={gesperrt}
                onClick={() => p.onAuftragAusAngebot(angebot)}>
                Auftrag anlegen
              </KiKnopf>
            )}
          </div>
        )}

        {/* Der Ablagestand — unter den Aktionen, in gedämpfter Farbe (Randnotiz, kein
            Alarm). NUR am Angebot, das der REIHE NACH (also über die Eigenschaft `angebot`,
            nicht nur den lokalen Stand) den Entwurf verlassen hat UND wirklich gesendet
            wurde (`sentOn`, Schlussrunde I1 — sonst erschiene an einem direkt aus dem
            Entwurf zurückgezogenen Angebot fälschlich „Noch nicht in der Akte abgelegt").
            Direkt nach „Als gesendet markieren" führt der Rahmen noch die alte (DRAFT-)
            Fassung, bis er neu lädt — die FRISCHE Auskunft steht in diesem Moment in der
            Meldung oben (`pdfMeldung`, siehe `statusSetzen`), nicht hier. Zwei Stellen, die
            im selben Wimpernschlag dasselbe sagten, wären eine Text-Dopplung, an der man
            beim nächsten Fix nur eine ändert. */}
        {!bearbeitung && angebot.status !== "DRAFT" && angebot.sentOn !== null
          && ablage !== undefined && (
          ablage === null
            ? (
              <p className="kang__ablage" role="status">
                Noch nicht in der Akte abgelegt.
                {" Mit „PDF in Akte ablegen“ lässt sich das nachholen."}
              </p>
            )
            : (
              <p className="kang__ablage" role="status">
                {`Abgelegt in der Akte am ${formatDatum(ablage.filedAt)} als „${ablage.fileName}" `}
                {`— zu finden im Reiter „Dateien" dieses Kunden.`}
              </p>
            )
        )}
      </div>

      {bearbeitung && (
        <Modal titel={`Angebot ${angebot.displayNumber} bearbeiten`} breite="weit"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kang__bearbeitung">
            {angebotFormularFelder({
              praefix: BEARBEITEN_PRAEFIX, mitUnternehmen: false, companies, umfeld,
              entwurf, setEntwurf, hatPositionen, currency: angebot.currency, faltungen, fehlerJeFeld,
              // Im Subdialog „Vorlage anwenden“ steht SEIN Fehlschlag dort — nicht doppelt.
              feldFehler: pruefung?.meldung
                ?? (anwendenWahl !== null && anwendenVersucht ? null : formular.meldung),
              formular: { ...formular.formularProps, hinweis: fremderStandHinweis() },
              vorschauWerte: angebotVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, angebot,
                kundeName: p.kundeName,
              }),
              /* Der Kopfblock PARTEIEN — `key` am Vorgang: Ein Wechsel des bearbeiteten
                 Angebots baut ihn neu auf, statt den Entwurf des vorigen weiterzureichen.
                 Nur beim BEARBEITEN, nicht beim Anlegen: Vor dem ersten Speichern gibt es
                 keinen Beleg, dessen Parteien man setzen könnte. */
              parteien: {
                // Der Käufer des Entwurfs, sobald jemand den Block angefasst hat, sonst der geladene.
                zusammenfassung: partiesSummary(loaded.parties, parteien.draft?.billingCustomerId, p.kundeName),
                inhalt: (
                  <BelegParteien key={`${angebot.id}-${String(standNeuLaden)}`}
                    client={client} art="offer"
                    customerId={angebot.customerId} vorgangId={angebot.id}
                    idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                    befunde={parteien.befunde} onEntwurf={parteien.melde} onLoaded={loaded.onPartiesLoaded} />
                ),
              },
              /* Der Abschnitt ZAHLUNGSVEREINBARUNG — `key` am Vorgang aus demselben Grund
                 wie an PARTEIEN: „Neue Version"/„Kopieren" wechseln die Kennung, und ein
                 Wechsel muss den Entwurf des VORIGEN Belegs verwerfen, statt seine (jetzt
                 fremden) Ratenkennungen weiterzutragen. */
              zahlplan: {
                zusammenfassung: ratesSummary(zahlplan.draft?.raten.length ?? loaded.rateCount),
                inhalt: (
                  <Zahlplan key={`${angebot.id}-zahlplan-${String(standNeuLaden)}`}
                    client={client}
                    bezug={{ art: "offer", customerId: angebot.customerId,
                             vorgangId: angebot.id }}
                    idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                    befunde={zahlplan.befunde} onEntwurf={zahlplan.melde}
                    onLoaded={loaded.onRatesLoaded} />
                ),
              },
              /* Der Kasten „Anlagen aus Textbausteinen" (465) — `key` am Vorgang aus demselben Grund wie an
                 PARTEIEN/ZAHLPLAN. DIESELBE Naht wie beim Positions-Editor: Jeder
                 Schreibweg des Kastens dreht die Belegversion weiter, und das nächste
                 „Speichern" muss sie kennen — sonst schickt es eine veraltete Version und
                 bekommt eine 409 für eine Änderung, die niemand gemacht hat. */
              anhaenge: {
                ...(loaded.attachmentCount === null ? {} : { zaehler: loaded.attachmentCount }),
                inhalt: (
                  <Anhaenge key={`${angebot.id}-anhaenge-${String(standNeuLaden)}`}
                    client={client}
                    bezug={{ art: "offer", customerId: angebot.customerId,
                             vorgangId: angebot.id }}
                    idPraefix={BEARBEITEN_PRAEFIX} verdeckt={formular.zustand !== "bereit"}
                    // Die AKTUELLE Belegversion geht HEREIN (Review-Befund zur Fixrunde
                    // K10, 24.09.2026): Positionen und Kopf-Speichern drehen `angebot.version`
                    // im selben Dialog weiter, ohne dass der Kasten selbst neu liest — ohne
                    // diese Eigenschaft liefe sein NÄCHSTER eigener Schreibweg (Ziehen,
                    // Entfernen, Anhängen) mit einer veralteten Version in eine 409.
                    version={angebot.version}
                    onVersionChange={(version) => {
                      p.onFortschreiben({ ...angebot, version });
                    }}
                    onLoaded={loaded.onAttachmentsLoaded} />
                ),
              },
              ansprechpartner: apClient === null ? undefined : {
                ...counterProp(loaded.representativeCount),
                inhalt: representativesBox(faltungen.representatives.offen, BEARBEITEN_PRAEFIX,
                  formular.zustand !== "bereit"),
              },
              /* Der grösste Block, in der Mitte der Maske (Entscheid Dirk 08.09.2026):
                 zwischen Einleitungs- und Ausleitungstext, in voller Breite. Die Positionen
                 hängen an DEMSELBEN Vorgang wie die Kopffelder darüber — jeder Schritt im
                 Editor dreht die Version des Angebots weiter, und `positionenGeaendert`
                 trägt sie über den Rahmen zurück, sonst liefe das nächste Speichern des
                 Kopfes in eine 409. */
              positionen: {
                ...itemsHead(positionen, angebot.currency),
                // „Vorlage anwenden“ steht im KOPF des Abschnitts — dort wirkt es, auch zugeklappt; sein Subdialog
                // daneben, nicht im (vielleicht versteckten) Inhalt.
                aktionen: (
                  <span>
                    <KiKnopf
                      deaktiviert={speichert || formular.zustand === "fremder-stand"}
                      onClick={() => { setAnwendenVersucht(false); setAnwendenWahl(""); }}>
                      Vorlage anwenden
                    </KiKnopf>
                    {/* Der Anwenden-Dialog sagt VORHER, was geschieht: Die Positionen kommen
                        ANS ENDE (nichts wird ersetzt), und die Texte übernimmt der Server nur,
                        wenn sie leer sind. Ohne diesen Satz wäre „anwenden" eine Wette darauf,
                        ob die eigene Arbeit stehen bleibt.

                        Seit ME-Etappe 3 (Aufgabe 4) ein SUBDIALOG über der Maske (me-dialog
                        `unter="seitlich"`, die Hülle wählt `stapel`, wo der Platz fehlt) statt
                        eines Kastens im Formular: Er hat eigene Ausgänge (Anwenden/Abbrechen),
                        und Escape schliesst NUR ihn — die Maske darunter behält ihren
                        Entwurf (Review-Fokus 4). */}
                    {anwendenWahl !== null && (
                      <Modal titel="Vorlage anwenden" onAbbrechen={() => { void anwendenAbbrechen(); }}
                        abbrechenGesperrt={speichert}>
                        <p className="kang__vorlagen-hinweis">
                          Die Positionen der Vorlage werden an die bestehende Liste angehängt;
                          Einleitungs- und Ausleitungstext werden nur übernommen, wenn sie leer
                          sind. Periodenregel und Zahlplan bleiben unverändert — sie kommen nur
                          mit, wenn das Angebot MIT der Vorlage angelegt wird. Noch nicht
                          gespeicherte Textänderungen ersetzt dabei der Stand vom Server.
                        </p>
                        {anwendenVersucht && formular.meldung !== null && (
                          <Hinweis art="fehler">{formular.meldung}</Hinweis>
                        )}
                        <Formular beschriftung="oben" laeuft={speichert}>
                          <Auswahl id="kang-anwenden-vorlage" name="kang-anwenden-vorlage"
                            beschriftung="Vorlage" wert={anwendenWahl}
                            onGeaendert={(d) => setAnwendenWahl(d.wert)}>
                            <option value="">Bitte wählen …</option>
                            {umfeld.vorlagen.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name} ({v.itemCount}{" "}
                                {v.itemCount === 1 ? "Position" : "Positionen"})
                              </option>
                            ))}
                          </Auswahl>
                        </Formular>
                        <ModalFuss speichernText="Anwenden"
                          speichernGesperrt={speichert || anwendenWahl === ""}
                          abbrechenGesperrt={speichert}
                          onSpeichern={() => { void vorlageAnwenden(anwendenWahl); }}
                          onAbbrechen={() => { void anwendenAbbrechen(); }} />
                      </Modal>
                    )}
                  </span>
                ),
                inhalt: (
                  <div className="kang__positionen">
                    <PositionsEditor
                      // Der Zähler im Schlüssel hängt den Editor nach einem Anwenden neu auf —
                      // nur so lädt er die angehängten Zeilen (er führt seinen Stand selbst).
                      key={`${angebot.id}-${String(positionenNeuLaden)}`}
                      client={client}
                      bezug={{ art: "offer", customerId: kundeId, vorgangId: angebot.id }}
                      companyId={angebot.companyId} version={angebot.version} waehrung={angebot.currency}
                      gesperrt={formular.zustand !== "bereit"}
                      onVersionChange={positionenGeaendert} />
                  </div>
                ),
              },
              dateien: filesContent({ ...files, onOrdnerOeffnen: folderFromPanel }, true, loaded),
              headExtra: (
                <ReplacesField id={`${BEARBEITEN_PRAEFIX}-replaces`} wert={replacesValue} onChange={setReplacesValue}
                  kandidaten={replacesCandidates(customerOffers, angebot.seriesId, angebot.replaces)}
                  error={fehlerJeFeld["replacesOfferId"]} />
              ),
            })}
          </div>

          {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die KOPFFELDER
              (die Positionen schreibt der Editor Schritt für Schritt selbst). */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}

      {copyDialogOpen && (
        <CopyOfferDialog quelleNummer={angebot.displayNumber} onKopieren={copyOffer}
          onAbbrechen={() => setCopyDialogOpen(false)} />
      )}

      {versionView !== null && (
        <VersionView client={client} kundeId={kundeId} version={versionView}
          onSchliessen={() => setVersionView(null)} />
      )}

      {vorschauOffen && (
        <Belegvorschau
          // Über denselben Helfer wie die übrigen Versionsanzeigen (M3, Schlussrunde):
          // Massgeblich ist `versionNumber`, nicht `versionCount` — die ERSTE Version
          // einer mehrversionigen Reihe trägt kein „v1" (versionsZusatz ist leer bei 1).
          titel={`Angebot ${angebot.displayNumber}`
            + `${versionsZusatz(angebot.versionNumber) !== ""
              ? ` ${versionsZusatz(angebot.versionNumber)}` : ""}`
            + " · Vorschau"}
          // Die FASSUNG: Die Version wechselt bei jeder Änderung, und genau dann ist das
          // gezeigte Blatt nicht mehr das aktuelle (Ruling K6).
          fassung={String(angebot.version)}
          laden={() => client.vorschauOffer(kundeId, angebot.id, { seitenbilder: false })}
          // Der PDF-Knopf der Vorschau geht den BESTEHENDEN Download-Weg — kein zweiter
          // Weg zum selben Ziel (Spec, wörtlich: „der bestehende Download-Weg").
          aufPdf={() => { void alsPdf(); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
    </>
  );
}
